import { Search, Filter, Users, Mail, Phone, MapPin, ChevronLeft, ChevronDown, ChevronUp, ChevronRight, UserPlus, BarChart3, MessageSquare, Plus, Send, Upload, CheckSquare, Square, Pencil, Eraser, Calendar, Activity, FileText, Settings, X, CreditCard } from 'lucide-react';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useToast } from '../../components/ui/Toast';
import { useSignedWaivers } from '../../hooks/useWaivers';
import { supabase } from '../../lib/supabase';
import { getFriendlyErrorMessage } from '../../utils/friendlyErrors';
import { UserSettingsModal } from '../../components/admin/UserSettingsModal';
import { OwnerEnrollmentModal } from '../../components/admin/OwnerEnrollmentModal';
import { AddStudentModal } from '../../components/admin/AddStudentModal';
import { usePermissions } from '../../hooks/usePermissions';
import { useCompanyUsers } from '../../hooks/useCompanyUsers';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { decodeJWTPayload } from '../../utils/jwt';

// Helper to get user-friendly status display (hides internal "registered" status for cross-gym users)
function getDisplayStatus(user: CompanyUser): string {
  if (!user.is_draft) return user.is_active ? 'Active' : 'Inactive';
  
  // For draft users: "registered" means they have an account elsewhere but haven't 
  // completed onboarding for THIS gym yet - show as "Invited" to gym owners
  if (user.status === 'registered' && user.user_id) {
    return 'Invited';
  }
  
  return user.status || 'Draft';
}

interface CompanyUser {
  user_id: string | null;
  user_name: string | null;
  email: string | null;
  role: string | null;
  role_key?: string | null;
  company_id: string | null;
  company_name: string | null;
  avatar_url: string | null;
  is_active: boolean | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  is_draft?: boolean;
  draft_id?: string;
  draft_email?: string | null;
  invite_token?: string;
  invite_sent_at?: string | null;
  status?: string;
  subscription_type?: string;
  primary_location_id?: string | null;
  location_name?: string | null;
  created_at?: string | null;
  original_start_date?: string | null;
  last_attendance_date?: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  is_child?: boolean | null;
  parent_user_id?: string | null;
  parent_id?: string | null;
  parent_name?: string | null;
  parent_email?: string | null;
  family_id?: string | null;
  is_family_lead?: boolean | null;
}

type QuickTab = 'all' | 'new' | 'draft' | 'no_subscription' | 'cancel_saves';

interface FilterState {
  quickTab: QuickTab;
  roles: string[];
  status: 'all' | 'active' | 'inactive' | 'draft' | 'invited' | 'registered';
  subscriptionType: string | null;
  locationId: string | null;
  registrationDateFrom: string | null;
  registrationDateTo: string | null;
  accountStatus: string | null;
}

export default function AccountManager() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    quickTab: 'all',
    roles: [],
    status: 'all',
    subscriptionType: null,
    locationId: null,
    registrationDateFrom: null,
    registrationDateTo: null,
    accountStatus: null,
  });
  const [locations, _setLocations] = useState<Array<{ id: string; name: string }>>([]);
  const [subscriptions, _setSubscriptions] = useState<Array<{ id: string; name: string }>>([]);
  const [draftChildrenCounts, setDraftChildrenCounts] = useState<Record<string, number>>({});
  const [viewMode, setViewMode] = useState<'all' | 'children' | 'families'>('all');
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const listContainerRef = React.useRef<HTMLDivElement>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [directInviteModal, setDirectInviteModal] = useState(false);
  const [directInviteEmail, setDirectInviteEmail] = useState('');
  const [directInviteFirstName, setDirectInviteFirstName] = useState('');
  const [directInviteLastName, setDirectInviteLastName] = useState('');
  const [directInvitePhone, setDirectInvitePhone] = useState('');
  const [directInviteLoading, setDirectInviteLoading] = useState(false);
  const [directInviteError, setDirectInviteError] = useState<string | null>(null);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [enrollmentProcessing, setEnrollmentProcessing] = useState(false);
  const { showToast, ToastContainer } = useToast();
  const pendingScrollToEmailRef = useRef<string | null>(null);
  const userRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Scroll to newly invited user after data reload
  const scrollToUserByEmail = useCallback((email: string) => {
    // Find the user by email in the current users list
    const user = users.find(u => 
      (u.email?.toLowerCase() === email.toLowerCase()) ||
      (u.draft_email?.toLowerCase() === email.toLowerCase())
    );
    if (user) {
      const userKey = user.draft_id || user.user_id || user.email || '';
      const rowElement = userRowRefs.current.get(userKey);
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight the row briefly
        rowElement.classList.add('ring-2', 'ring-[#9B1421]', 'ring-opacity-75');
        setTimeout(() => {
          rowElement.classList.remove('ring-2', 'ring-[#9B1421]', 'ring-opacity-75');
        }, 2000);
      }
    }
  }, [users]);

  // Check for pending scroll after users update
  useEffect(() => {
    if (pendingScrollToEmailRef.current && users.length > 0) {
      // Small delay to ensure DOM is updated
      const emailToScroll = pendingScrollToEmailRef.current;
      const timer = setTimeout(() => {
        if (emailToScroll) {
          scrollToUserByEmail(emailToScroll);
        }
        pendingScrollToEmailRef.current = null;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [users, scrollToUserByEmail]);

  // Get company ID from JWT claims (not from database - ensures it matches current company context)
  // This runs on mount and whenever session changes (e.g., after gym switch)
  useEffect(() => {
    const getCompanyIdFromJWT = async (retryCount = 0) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signin');
        return;
      }

      // Read company_id from JWT claims (source of truth for current company context)
      const claims = decodeJWTPayload(session.access_token);
      const jwtCompanyId = claims?.company_id;

      if (jwtCompanyId) {
        setCompanyId(jwtCompanyId);
        return;
      }

      // JWT doesn't have company_id - try refreshing session (up to 2 retries)
      if (retryCount < 2) {
        console.warn(`[AccountManager] JWT missing company_id, refreshing session (attempt ${retryCount + 1}/2)...`);
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError) {
          // Wait for JWT to propagate (auth hook needs to process last_selected_company)
          await new Promise(resolve => setTimeout(resolve, 500));
          // Retry getting company ID
          return getCompanyIdFromJWT(retryCount + 1);
        }
      }

      // After retries, if still no company_id, boot user out (no workaround queries)
      console.error('[AccountManager] JWT missing company_id after retries - booting user out');
      showToast('Session error: Please sign in again.', 'error');
      await supabase.auth.signOut();
      navigate('/signin');
    };

    getCompanyIdFromJWT();

    // Listen for auth state changes (e.g., after gym switch and session refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        // JWT was refreshed - get new company ID from updated JWT
        if (session?.access_token) {
          const claims = decodeJWTPayload(session.access_token);
          const jwtCompanyId = claims?.company_id;
          if (jwtCompanyId) {
            setCompanyId(jwtCompanyId);
          }
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, showToast]);

  // Use TanStack Query hook for company users (must be at component level)
  const companyUsersQuery = useCompanyUsers(companyId);

  // Update local state when query data changes
  useEffect(() => {
    if (companyUsersQuery.data) {
      // companyUsersQuery.data.users already includes all users:
      // - Regular users (from user_company_relationships)
      // - Draft parents (from family_leads_view)
      // - Draft children (added in useCompanyUsers hook)
      // So we just use it directly without adding duplicates
      const allUsers = companyUsersQuery.data.users;
      setUsers(allUsers);
      setDraftChildrenCounts(companyUsersQuery.data.draftChildrenCounts);
      setLoading(false);

      // Update selectedUser if it exists in the new data
      if (selectedUser) {
        const updatedUser = allUsers.find(
          (u) => (u.user_id && u.user_id === selectedUser.user_id) ||
                 (u.draft_id && u.draft_id === selectedUser.draft_id)
        );
        if (updatedUser) {
          setSelectedUser(updatedUser);
        }
      }
    } else if (companyUsersQuery.isLoading) {
      setLoading(true);
    } else if (companyUsersQuery.isError) {
      setLoading(false);
      console.error('[AccountManager] Error loading users:', companyUsersQuery.error);
    }
  }, [companyUsersQuery.data, companyUsersQuery.isLoading, companyUsersQuery.isError, companyUsersQuery.error, selectedUser]);

  // Check for query parameters on mount to set filters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    const status = urlParams.get('status');
    
    if (tab === 'draft') {
      setFilters(prev => ({ ...prev, quickTab: 'draft' }));
    }
    
    // Handle status filter from URL (e.g., from OnboardingDashboard KPI cards)
    if (status && ['draft', 'invited', 'registered'].includes(status)) {
      setFilters(prev => ({ ...prev, status: status as 'draft' | 'invited' | 'registered' }));
    }
  }, []);
  
  // Check for userId query parameter to auto-select a user
  // This effect runs whenever the URL search params change (including when already on the page)
  useEffect(() => {
    const userId = searchParams.get('userId');
    if (userId && users.length > 0) {
      // Only auto-select if we don't already have a selected user, OR if the userId in URL is different
      const shouldSelect = !selectedUser || (selectedUser.user_id !== userId && selectedUser.draft_id !== userId);
      if (shouldSelect) {
        const userToSelect = users.find(
          u => u.user_id === userId || u.draft_id === userId
        );
        if (userToSelect) {
          setSelectedUser(userToSelect);
          setShowDetail(true);
          // Clear the query param after selecting
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('userId');
          setSearchParams(newParams, { replace: true });
        } else {
          // User not found - clear the query param to prevent retrying
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('userId');
          setSearchParams(newParams, { replace: true });
        }
      }
    }
  }, [searchParams, users, selectedUser, setSearchParams]);

  // Handle enrollment_session_id return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('enrollment_session_id');
    if (sessionId && companyId && !enrollmentProcessing) {
      // Remove param from URL immediately
      window.history.replaceState({}, '', window.location.pathname);
      // Process the enrollment
      processOwnerEnrollment(sessionId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const processOwnerEnrollment = async (sessionId: string) => {
    setEnrollmentProcessing(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('process-owner-enrollment', {
        body: { sessionId, companyId },
      });

      if (invokeError) {
        // Extract the actual error message from the response body
        let errorMessage = 'Failed to process enrollment. Please try again.';
        try {
          const errorBody = await invokeError.context?.json?.();
          if (errorBody?.error) {
            errorMessage = errorBody.payment_failed
              ? `Payment declined: ${errorBody.error}`
              : errorBody.error;
          }
        } catch {
          // Response body not parseable, use default message
        }
        console.error('Error processing owner enrollment:', invokeError);
        showToast(errorMessage, 'error');
        return;
      }
      if (!data?.success) {
        if (data?.requires_action) {
          showToast('Payment requires additional authentication. Please contact the student.', 'error');
          return;
        }
        throw new Error(data?.error || 'Failed to process enrollment');
      }

      showToast('Student enrolled successfully!', 'success');
      // Refresh the user list
      await companyUsersQuery.refetch();
    } catch (err: any) {
      console.error('Error processing owner enrollment:', err);
      showToast(err?.message || 'Failed to process enrollment. Please try again.', 'error');
    } finally {
      setEnrollmentProcessing(false);
    }
  };

  // Prevent body and main content scrolling when modal is open
  useEffect(() => {
    if (showDetail && selectedUser) {
      // Find the main scroll container (from AuthenticatedLayout)
      const mainElement = document.querySelector('main');
      const htmlElement = document.documentElement;
      const bodyElement = document.body;
      
      // Save current scroll positions
      const scrollY = window.scrollY;
      const mainScrollTop = mainElement?.scrollTop || 0;
      
      // Prevent scrolling on body
      bodyElement.style.position = 'fixed';
      bodyElement.style.top = `-${scrollY}px`;
      bodyElement.style.width = '100%';
      bodyElement.style.overflow = 'hidden';
      
      // Prevent scrolling on html
      htmlElement.style.overflow = 'hidden';
      
      // Prevent scrolling on main element if it exists
      if (mainElement) {
        mainElement.style.overflow = 'hidden';
        mainElement.style.position = 'relative';
      }
      
      return () => {
        // Restore scrolling on body
        bodyElement.style.position = '';
        bodyElement.style.top = '';
        bodyElement.style.width = '';
        bodyElement.style.overflow = '';
        
        // Restore scrolling on html
        htmlElement.style.overflow = '';
        
        // Restore scrolling on main element
        if (mainElement) {
          mainElement.style.overflow = '';
          mainElement.style.position = '';
          mainElement.scrollTop = mainScrollTop;
        }
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [showDetail, selectedUser]);

  const handleUserClick = (user: CompanyUser) => {
    // Clear any userId query parameter from URL when opening detail view
    if (searchParams.has('userId')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('userId', user.user_id || '');
      setSearchParams(newParams, { replace: true });
    } else if (user.user_id) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('userId', user.user_id);
      setSearchParams(newParams, { replace: true });
    }
    
    setSelectedUser(user);
    setShowDetail(true);
  };

  const handleBack = () => {
    // Clear any userId query parameter from URL when closing detail view
    if (searchParams.has('userId')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('userId');
      setSearchParams(newParams, { replace: true });
    }
    
    setSelectedUser(null);
    setShowDetail(false);
  };

  // Selection handlers
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    // Get selectable users (exclude children unless in "Children" view mode)
    const selectableUsers = filteredUsers.filter(u => {
      if (u.is_child === true && viewMode !== 'children') {
        return false;
      }
      return true;
    });
    
    const selectableIds = new Set(selectableUsers.map(u => u.user_id || u.draft_id || '').filter(Boolean));
    
    if (selectableIds.size > 0 && Array.from(selectableIds).every(id => selectedUsers.has(id))) {
      // Deselect all selectable users
      const newSet = new Set(selectedUsers);
      selectableIds.forEach(id => newSet.delete(id));
      setSelectedUsers(newSet);
    } else {
      // Select all selectable users
      const newSet = new Set(selectedUsers);
      selectableIds.forEach(id => newSet.add(id));
      setSelectedUsers(newSet);
    }
  };

  // Send invite to a single draft student
  const handleSendDraftInvite = async (draftStudentId: string, email: string, hasInviteSent: boolean) => {
    if (!draftStudentId || !email) {
      showToast('No email found for this draft student.', 'error');
      return;
    }

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated');

      // Always call send-draft-student-invite - the edge function automatically handles:
      // 1. If user is confirmed → automatically sends magic link
      // 2. If user is unconfirmed → deletes unconfirmed user and sends new invite
      // 3. If no user exists → sends new invite
      // This eliminates the need for user choice since the logic is handled server-side
      // CRITICAL: Pass companyId to validate draft record belongs to correct company
      // This prevents bugs where draft records have wrong company_id due to user switching companies
      const { data, error } = await supabase.functions.invoke('send-draft-student-invite', {
        body: {
          draftStudentId,
          adminUserId: currentUser.id,
          companyId, // Pass current company context to validate draft record
        },
      });

      if (error) throw error;
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to send invite');
      }

      // Show appropriate message based on what was sent
      if (data?.usedMagicLink) {
        showToast(`Magic link sent to ${email}. This student already has an account.`, 'success');
      } else if (hasInviteSent) {
        showToast(`Invitation re-sent to ${email}`, 'success');
      } else {
        showToast(`Invitation sent to ${email}`, 'success');
      }

      await companyUsersQuery.refetch();
    } catch (err: unknown) {
      console.error('Error sending invite:', err);
      showToast(getFriendlyErrorMessage(err, 'invite'), 'error');
    }
  };

  const handleBulkSendInvites = async () => {
    const selectedDraftStudents = filteredUsers.filter(u => 
      selectedUsers.has(u.draft_id || '') && u.is_draft && u.draft_id
    );

    if (selectedDraftStudents.length === 0) {
      showToast('No draft students selected.', 'error');
      return;
    }

    // If only one user selected, use single invite function
    if (selectedDraftStudents.length === 1) {
      const student = selectedDraftStudents[0];
      if (!student.draft_id) {
        showToast('Selected student has no draft_id.', 'error');
        return;
      }
      const hasInviteSent = student.status === 'invited' || !!student.invite_sent_at;
      await handleSendDraftInvite(student.draft_id, student.email || student.draft_email || '', hasInviteSent);
      setSelectedUsers(new Set());
      return;
    }

    // Multiple users - use bulk function
    const studentText = selectedDraftStudents.length !== 1 ? 's' : '';
    const confirmMessage = `Send invitations to ${selectedDraftStudents.length} selected draft student${studentText}?\n\nInvites will be processed in the background. You can continue using the app.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setBulkActionLoading(true);
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated');

      // Use the gym this page is showing — the same `companyId` every query
      // above filters by, and the same one handleSendDraftInvite already
      // passes. This re-derived it as `user_company_relationships … .limit(1)`
      // with no ORDER BY and no reference to the selected company, so for a
      // multi-gym admin it was an arbitrary row that did not change when they
      // switched gyms: bulk invites went out branded for, and filed under, the
      // wrong gym while the roster on screen said otherwise.
      if (!companyId) throw new Error('Company not found');

      const { data, error } = await supabase.functions.invoke('send-bulk-draft-invites', {
        body: {
          draftStudentIds: selectedDraftStudents.map(s => s.draft_id).filter((id): id is string => Boolean(id)),
          adminUserId: currentUser.id,
          companyId,
        },
      });

      if (error) throw error;

      const result = data as {
        success: boolean;
        // Absent when the batch was queued as a background job (>10 invites).
        sent_count?: number;
        resent_count?: number;
        error_count?: number;
        queued?: boolean;
        errors?: Array<{id: string, email: string, error: string}>;
      };

      const sentCount = result.sent_count ?? 0;
      const resentCount = result.resent_count ?? 0;
      const errorCount = result.error_count ?? 0;

      let message = '';
      if (sentCount > 0) {
        const sentText = sentCount !== 1 ? 's' : '';
        message += `${sentCount} invitation${sentText} sent. `;
      }
      if (resentCount > 0) {
        const resentText = resentCount !== 1 ? 's' : '';
        message += `${resentCount} invitation${resentText} resent. `;
      }
      if (errorCount > 0) {
        const failedText = errorCount !== 1 ? 's' : '';
        message += `${errorCount} invitation${failedText} failed.`;
        if (result.errors && result.errors.length > 0) {
          const errorDetails = result.errors.slice(0, 5).map(e => `  • ${e.email}: ${e.error}`).join('\n');
          message += `\n\nFirst few errors:\n${errorDetails}`;
          if (result.errors.length > 5) {
            message += `\n  ... and ${result.errors.length - 5} more`;
          }
        }
      }

      // "Nothing sent" is not "sent". The edge function selects the drafts
      // with `.in('id', ids).eq('company_id', companyId)`, so a company that
      // does not own them matches zero rows — which is an empty array, not an
      // error. That returned 0/0/0, `message` stayed empty, and this fell
      // through to the success branch: a green "Invitations sent
      // successfully!" for an empty batch. Six invites went out as two.
      //
      // Batches over 10 come back as `{ queued: true }` with no counts at all,
      // and those genuinely did start — they are excluded before the count
      // check rather than left to fall through as NaN.
      if (result.queued) {
        showToast(`${selectedDraftStudents.length} invitations queued — they will send in the background.`, 'info');
      } else if (sentCount + resentCount + errorCount === 0) {
        showToast(
          `No invitations were sent — none of the ${selectedDraftStudents.length} selected students could be matched. Please try again or report this.`,
          'error',
        );
      } else if (errorCount > 0 && sentCount === 0 && resentCount === 0) {
        showToast(message.trim() || 'Failed to send invitations', 'error');
      } else if (errorCount > 0) {
        showToast(message.trim() || 'Some invitations failed', 'info');
      } else {
        showToast(message.trim() || 'Invitations sent successfully!', 'success');
      }
      
      // Clear selection and reload data
      setSelectedUsers(new Set());
      await companyUsersQuery.refetch();
    } catch (err: unknown) {
      console.error('Error sending bulk invites:', err);
      showToast(getFriendlyErrorMessage(err, 'invite'), 'error');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkSendEmail = () => {
    // Separate adults and children
    const selectedAdults = filteredUsers.filter(u => {
      const id = u.user_id || u.draft_id || '';
      return selectedUsers.has(id) && u.is_child !== true;
    });
    
    const selectedChildren = filteredUsers.filter(u => {
      const id = u.user_id || u.draft_id || '';
      return selectedUsers.has(id) && u.is_child === true;
    });
    
    // Collect adult emails
    const adultEmails = Array.from(
      new Set(
        selectedAdults
          .map(user => (user.email || user.draft_email || '').trim())
          .filter(Boolean)
      )
    );
    
    // Collect parent emails from selected children (deduplicated)
    const parentEmails = Array.from(
      new Set(
        selectedChildren
          .map(child => {
            // Use parent_email if available, otherwise look up from users array
            if (child.parent_email) {
              return child.parent_email.trim();
            }
            /* Fallback: get parent email from the parent_user_id */
            const parent = users.find(u => u.user_id === child.parent_user_id);
            return parent?.email?.trim() || '';
          })
          .filter(Boolean)
      )
    );
    
    // Combine all emails (adults + parents of children)
    const allEmails = Array.from(new Set([...adultEmails, ...parentEmails]));
    
    if (allEmails.length === 0) {
      showToast('No email addresses found. None of the selected users have email addresses in our system.', 'error');
      return;
    }
    
    // Build informative message
    let message = '';
    if (selectedChildren.length > 0 && selectedAdults.length > 0) {
      const userText = selectedAdults.length !== 1 ? 's' : '';
      const parentText = parentEmails.length !== 1 ? 's' : '';
      const childText = selectedChildren.length !== 1 ? 'ren' : '';
      message = `Emailing ${selectedAdults.length} user${userText} and ${parentEmails.length} parent${parentText} of ${selectedChildren.length} child${childText}.`;
    } else if (selectedChildren.length > 0) {
      const parentText = parentEmails.length !== 1 ? 's' : '';
      const childText = selectedChildren.length !== 1 ? 'ren' : '';
      message = `Emailing ${parentEmails.length} parent${parentText} of ${selectedChildren.length} selected child${childText}.`;
    } else {
      const userText = selectedAdults.length !== 1 ? 's' : '';
      message = `Emailing ${selectedAdults.length} user${userText}.`;
    }
    
    const subject = encodeURIComponent(`Message from ${users[0]?.company_name || 'Your Gym'}`);
    const body = encodeURIComponent('Hello,\n\n');
    const mailtoUrl = `mailto:?to=${allEmails.join(',')}&subject=${subject}&body=${body}`;
    
    if (selectedChildren.length > 0 || selectedAdults.length > 1) {
      if (!window.confirm(message)) {
        return;
      }
    }
    
    window.open(mailtoUrl, '_blank');
    
    // Clear selection
    setSelectedUsers(new Set());
  };

  const handleDirectInvite = async () => {
    const trimmedEmail = directInviteEmail.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setDirectInviteError('Please enter a valid email address.');
      return;
    }

    try {
      setDirectInviteLoading(true);
      setDirectInviteError(null);

      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated');

      // Same fix as the bulk path above: send the gym the admin is looking at,
      // not an arbitrary `user_company_relationships` row. For a multi-gym
      // admin this resolved to whichever membership Postgres returned first,
      // so direct invites arrived branded for, and filed under, the wrong gym.
      if (!companyId) throw new Error('Company not found');

      // Use fetch directly to always get the response body (supabase.functions.invoke
      // throws before parsing body on non-2xx responses)
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL || 'http://127.0.0.1:54321'}/functions/v1/send-direct-invite`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email: trimmedEmail,
          companyId,
          adminUserId: currentUser.id,
          firstName: directInviteFirstName.trim() || null,
          lastName: directInviteLastName.trim() || null,
          phoneNumber: directInvitePhone.trim() || null,
        }),
      });

      const data = await response.json();
      
      // Check for success
      if (data?.success) {
        showToast(data.message || `Invitation sent to ${trimmedEmail}`, 'success');
        setDirectInviteModal(false);
        setDirectInviteEmail('');
        setDirectInviteFirstName('');
        setDirectInviteLastName('');
        setDirectInvitePhone('');
        // Set pending scroll to the newly invited user
        pendingScrollToEmailRef.current = trimmedEmail;
        await companyUsersQuery.refetch();
        return;
      }
      
      // Handle error - throw with the actual error message from the function
      throw new Error(data?.error || 'invite_failed');
    } catch (err: unknown) {
      console.error('Error sending direct invite:', err);
      setDirectInviteError(getFriendlyErrorMessage(err, 'invite'));
    } finally {
      setDirectInviteLoading(false);
    }
  };

  // Simplified loadData - just refetches the TanStack Query
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _loadData = async () => {
    if (companyId && companyUsersQuery) {
      await companyUsersQuery.refetch();
      
      // Load locations and subscriptions for filters (these don't change often)
      const { data: locationsData } = await supabase
        .from('locations')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name', { ascending: true });
      if (locationsData) {
        _setLocations(locationsData.filter((loc): loc is { id: string; name: string } => loc.name !== null));
      }
      
      const { data: subscriptionsData } = await supabase
        .from('company_subscriptions')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name', { ascending: true });
      if (subscriptionsData) {
        _setSubscriptions(subscriptionsData.filter((sub): sub is { id: string; name: string } => sub.name !== null));
      }
    }
  };

  // Legacy loadData function - keeping for reference but should be removed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const __oldLoadData = async () => {
    try {
      setLoading(true);
      
      // Get current user's company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: relationships } = await supabase
        .from('user_company_relationships')
        .select('company_id, companies!inner(id, name)')
        .eq('user_id', user.id)
        .limit(1)
        .single();

      if (!relationships?.company_id) {
        setLoading(false);
        return;
      }

      const companyId = relationships.company_id;

      // Fetch users using the same RPC as mobile app
      // RPC function not in generated types - get_company_users is a database function
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: usersData, error: usersError } = await (supabase as any)
        .rpc('get_company_users', { p_company_id: companyId });

      if (usersError) throw usersError;

      // Fetch draft students from family_leads_view
      /* Show all drafts that aren't fully registered yet (status != 'registered') */
      // View not in generated types - family_leads_view is a database view
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: draftData, error: draftError } = await (supabase as any)
        .from('family_leads_view')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_archived', false)
        .or('user_id.is.null,status.neq.registered'); /* Show all drafts that aren't fully registered */

      if (draftError) console.error('Error fetching drafts:', draftError);

      // Fetch draft children counts for draft students with family_id
      const draftChildrenCountsMap: Record<string, number> = {};
      if (draftData && draftData.length > 0) {
        // Count children for each draft student (excluding the parent record itself)
        // Children are identified as records with the same family_id but different email from the parent
        for (const draft of draftData) {
          if (draft.family_id && draft.family_id !== '' && draft.id && draft.email) {
            // Get all records with this family_id
            // Table query - using any for simplicity, could be typed with Database types
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data: familyMembers } = await (supabase as any)
              .from('staging_import_members')
              .select('id, email')
              .eq('company_id', companyId)
              .eq('family_id', draft.family_id)
              .eq('is_archived', false);
            
            if (familyMembers && familyMembers.length > 1) {
              // Find the first record with this email (the family lead for this email)
              // Note: Currently unused but may be needed for future family lead logic
              // const firstRecordWithEmail = familyMembers.find((m: any) => 
              //   m.email && m.email.toLowerCase() === draft.email.toLowerCase()
              // );
              
              // Count children: records with same family_id but different email, or no email
              // AND are unregistered
              // Table query - using any for simplicity
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const { data: childrenData } = await (supabase as any)
                .from('staging_import_members')
                .select('id, email, user_id')
                .eq('company_id', companyId)
                .eq('family_id', draft.family_id)
                .neq('id', draft.id) // Exclude the parent record
                .is('user_id', null) // Only count unregistered children
                .eq('is_archived', false);
              
              if (childrenData) {
                // Filter to only show children (different email or no email)
                interface ChildRecord {
                  id: string;
                  email: string | null;
                  user_id: string | null;
                }
                const actualChildren = (childrenData as ChildRecord[]).filter((c) => 
                  !c.email || c.email.toLowerCase() !== draft.email.toLowerCase()
                );
                draftChildrenCountsMap[draft.id] = actualChildren.length;
              }
            }
          }
        }
      }
      setDraftChildrenCounts(draftChildrenCountsMap);

      // Combine users and drafts
      // RPC/View data needs type assertion - data shape matches CompanyUser interface
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // RPC/View return types not in generated types - using type assertion
      const allUsers: CompanyUser[] = [
        ...((usersData as unknown[]) || []).map((u) => {
          const user = u as Record<string, unknown>;
          return {
            ...user,
            is_draft: false,
            contract_start_date: (user.contract_start_date as string) || null,
            contract_end_date: (user.contract_end_date as string) || null,
          } as CompanyUser;
        }),
        ...((draftData as unknown[]) || []).map((d) => {
          const draft = d as Record<string, unknown>;
          return {
            user_id: draft.user_id as string | null,
            user_name: (draft.first_name && draft.last_name) ? `${draft.first_name} ${draft.last_name}` : null,
            email: draft.email as string | null,
            role: 'draft',
            role_key: 'draft',
            company_id: draft.company_id as string | null,
            company_name: draft.company_name as string | null,
            avatar_url: null,
            is_active: draft.status === 'registered',
            first_name: draft.first_name as string | null,
            last_name: draft.last_name as string | null,
            phone: draft.phone_number as string | null,
            is_draft: true,
            draft_id: draft.id as string,
            draft_email: draft.email as string | null,
            invite_token: draft.invite_token as string | undefined,
            invite_sent_at: draft.invite_sent_at as string | null | undefined,
            status: draft.computed_status as string | undefined,
            subscription_type: draft.subscription_name as string | undefined,
            primary_location_id: draft.primary_location_id as string | null,
            location_name: draft.location_name as string | null,
            created_at: draft.created_at as string | null,
            original_start_date: draft.original_start_date as string | null,
            last_attendance_date: null,
          } as CompanyUser;
        }),
      ];

      setUsers(allUsers);
      
      // Load locations and subscriptions for filters
      const { data: locationsData } = await supabase
        .from('locations')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name', { ascending: true });
      if (locationsData) {
        _setLocations(locationsData.filter((loc): loc is { id: string; name: string } => loc.name !== null));
      }
      
      const { data: subscriptionsData } = await supabase
        .from('company_subscriptions')
        .select('id, name')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .eq('is_deleted', false)
        .order('name', { ascending: true });
      if (subscriptionsData) {
        _setSubscriptions(subscriptionsData.filter((sub): sub is { id: string; name: string } => sub.name !== null));
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    // Quick tab filters
    if (filters.quickTab !== 'all') {
      if (filters.quickTab === 'new') {
        // New students: registered in last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        filtered = filtered.filter(user => {
          const userDate = user.created_at || user.original_start_date;
          return userDate && new Date(userDate) >= thirtyDaysAgo && !user.is_draft;
        });
      } else if (filters.quickTab === 'draft') {
        // Draft students
        filtered = filtered.filter(user => user.is_draft);
      } else if (filters.quickTab === 'no_subscription') {
        // Students without a subscription
        filtered = filtered.filter(user => 
          !user.is_draft && 
          (!user.subscription_type || user.subscription_type === '' || user.subscription_type.toLowerCase() === 'no plan')
        );
      } else if (filters.quickTab === 'cancel_saves') {
        // Cancel Saves: Students who have not attended a class in more than 30 days
        // Uses last_attendance_date from get_company_users (calculated from class_enrollments filtered by company)
        // Only includes students - excludes employees (owner, admin, coach, reception) who don't attend classes as students
        const now = new Date();
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        // Normalize to midnight UTC for date comparison
        thirtyDaysAgo.setHours(0, 0, 0, 0);
        
        filtered = filtered.filter(user => {
          // Only include students - exclude employees (owner, admin, coach, reception)
          const userRole = (user.role_key || user.role || '').toLowerCase();
          if (userRole !== 'student') return false;
          
          // Exclude draft students (they haven't registered yet)
          if (user.is_draft) return false;
          // Exclude children (their attendance is tracked via parent)
          if (user.is_child === true) return false;
          // If they've never attended at this company, include them (no attendance = cancel save candidate)
          if (!user.last_attendance_date) return true;
          // Normalize last attendance date to midnight UTC for comparison
          const lastAttendance = new Date(user.last_attendance_date);
          lastAttendance.setHours(0, 0, 0, 0);
          // Include if last attendance was more than 30 days ago (strictly less than)
          return lastAttendance < thirtyDaysAgo;
        });
      }
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => {
        const name = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        const subscription = (user.subscription_type || '').toLowerCase();
        return name.includes(query) || email.includes(query) || phone.includes(query) || subscription.includes(query);
      });
    }

    // Role filter
    if (filters.roles.length > 0) {
      filtered = filtered.filter(user => filters.roles.includes(user.role || ''));
    }

    // Status filter
    if (filters.status !== 'all') {
      if (filters.status === 'active') {
        filtered = filtered.filter(user => user.is_active === true && !user.is_draft);
      } else if (filters.status === 'inactive') {
        filtered = filtered.filter(user => user.is_active === false && !user.is_draft);
      } else if (filters.status === 'draft') {
        filtered = filtered.filter(user => user.is_draft && user.status === 'draft');
      } else if (filters.status === 'invited') {
        filtered = filtered.filter(user => user.is_draft && user.status === 'invited');
      } else if (filters.status === 'registered') {
        // Registered drafts: have user_id but haven't completed onboarding for THIS gym
        filtered = filtered.filter(user => user.is_draft && user.status === 'registered' && user.user_id);
      }
    }

    // Subscription filter
    if (filters.subscriptionType) {
      filtered = filtered.filter(user => user.subscription_type === filters.subscriptionType);
    }

    // Location filter
    if (filters.locationId) {
      filtered = filtered.filter(user => user.primary_location_id === filters.locationId);
    }

    // Registration date filter
    if (filters.registrationDateFrom) {
      const fromDate = filters.registrationDateFrom;
      filtered = filtered.filter(user => {
        const userDate = user.created_at || user.original_start_date;
        return userDate && new Date(userDate) >= new Date(fromDate);
      });
    }

    // View mode filter
    if (viewMode === 'children') {
      // Children only view: return only children
      filtered = filtered.filter(user => user.is_child === true);
    } else if (viewMode === 'families') {
      // Families view: show parents (registered and drafts) who have at least one child
      const result: CompanyUser[] = [];
      
      // Separate registered users, children, and drafts
      const registeredUsers = filtered.filter(u => !u.is_draft && !u.is_child);
      const allChildren = filtered.filter(u => u.is_child === true);
      const draftParents = filtered.filter(u => u.is_draft === true && u.is_child !== true);
      const draftChildren = filtered.filter(u => u.is_draft === true && u.is_child === true);
      
      // Group registered children by parent user_id
      const registeredChildren = allChildren.filter(c => !c.is_draft);
      const childrenByParent = new Map<string, CompanyUser[]>();
      registeredChildren.forEach(child => {
        const parentId = child.parent_user_id || child.parent_id || '';
        if (parentId) {
          if (!childrenByParent.has(parentId)) {
            childrenByParent.set(parentId, []);
          }
          const children = childrenByParent.get(parentId);
          if (children) {
            children.push(child);
          }
        }
      });
      
      // Group draft children by family_id
      const draftChildrenByFamily = new Map<string, CompanyUser[]>();
      draftChildren.forEach(child => {
        const familyId = child.family_id || '';
        if (familyId && familyId !== '') {
          if (!draftChildrenByFamily.has(familyId)) {
            draftChildrenByFamily.set(familyId, []);
          }
          const draftChildren = draftChildrenByFamily.get(familyId);
          if (draftChildren) {
            draftChildren.push(child);
          }
        }
      });
      
      // Add registered parents who have children
      registeredUsers.forEach(parent => {
        const parentId = parent.user_id || '';
        const parentChildren = childrenByParent.get(parentId) || [];
        if (parentChildren.length > 0) {
          result.push(parent);
        }
      });
      
      // Add draft parents who have children (family groups with >1 member)
      // Only show family leads whose family_id has at least one child
      // Direct invites (no family_id) are individuals, not families — skip them

      // Group drafts by family_id and email to handle cases where multiple people share the same email
      const draftsByFamilyEmail = new Map<string, CompanyUser[]>();
      draftParents.forEach(draft => {
        const familyId = draft.family_id || '';
        if (!familyId) return; // Skip individuals — they're not families
        const email = (draft.email || '').toLowerCase();
        const key = `${familyId}:${email}`;

        if (!draftsByFamilyEmail.has(key)) {
          draftsByFamilyEmail.set(key, []);
        }
        const drafts = draftsByFamilyEmail.get(key);
        if (drafts) {
          drafts.push(draft);
        }
      });

      // For each family/email group, only show if they have children
      draftsByFamilyEmail.forEach((draftsInGroup) => {
        const familyId = draftsInGroup[0]?.family_id || '';
        const familyChildren = draftChildrenByFamily.get(familyId) || [];

        // Only show families with at least one child (>1 total member)
        if (familyChildren.length === 0) return;

        // Prioritize the one marked as family lead
        const familyLead = draftsInGroup.find(d => d.is_family_lead === true);
        if (familyLead) {
          result.push(familyLead);
        } else if (draftsInGroup.length > 0) {
          // If no one is marked as lead, show the first one with an email
          const withEmail = draftsInGroup.find(d => d.email && d.email !== '');
          if (withEmail) {
            result.push(withEmail);
          } else {
            result.push(draftsInGroup[0]);
          }
        }
      });
      
      filtered = result;
    } else if (viewMode === 'all') {
      // 'all' mode: show only family heads (parents), hide children by default
      // Children will be shown when family is expanded
      filtered = filtered.filter(user => {
        // Show registered users who are not children
        if (!user.is_draft && user.is_child !== true) return true;
        // Show draft parents (family leads or direct invites)
        if (user.is_draft && user.is_child !== true) {
          const familyId = user.family_id || '';
          const isDirectInvite = !familyId || familyId === '';
          
          if (isDirectInvite) {
            // Direct invites: always show
            return true;
          }
          
          // For family groups: only show if this user is the family lead
          // This prevents showing multiple people with the same email
          if (familyId !== '') {
            // Check if there are other users in the same family with the same email
            const sameFamilyEmail = users.filter(u => 
              u.is_draft && 
              u.family_id === familyId && 
              (u.email || '').toLowerCase() === (user.email || '').toLowerCase() &&
              u.draft_id !== user.draft_id
            );
            
            if (sameFamilyEmail.length > 0) {
              // Multiple people with same email in family: only show the family lead
              return user.is_family_lead === true;
            } else {
              // Only one person with this email in family: show if they have email or are marked as lead
              return user.is_family_lead === true || (user.email && user.email !== '');
            }
          }
          
          return false;
        }
        // Hide children in 'all' view (they'll show when family is expanded)
        return false;
      });
    }

    return filtered;
  }, [users, searchQuery, filters, viewMode]);

  // Group children by family for children view
  const childrenByFamily = useMemo(() => {
    if (viewMode !== 'children') return new Map<string, CompanyUser[]>();
    
    const familyMap = new Map<string, CompanyUser[]>();
    const allChildren = users.filter(u => u.is_child === true);
    
    allChildren.forEach(child => {
      // Group by last name for registered children, or family_id for draft children
      let familyKey = '';
      if (child.is_draft && child.family_id) {
        familyKey = child.family_id;
      } else if (child.last_name) {
        familyKey = child.last_name.toLowerCase();
      } else {
        familyKey = 'no-family';
      }
      
      if (!familyMap.has(familyKey)) {
        familyMap.set(familyKey, []);
      }
      const familyChildren = familyMap.get(familyKey);
      if (familyChildren) {
        familyChildren.push(child);
      }
    });
    
    return familyMap;
  }, [users, viewMode]);

  // Get all children for expand/collapse in all/families view
  const allChildrenByParent = useMemo(() => {
    if (viewMode === 'children') return new Map<string, CompanyUser[]>();
    
    const parentMap = new Map<string, CompanyUser[]>();
    const allChildren = users.filter(u => u.is_child === true);
    
    allChildren.forEach(child => {
      let parentKey = '';
      if (child.is_draft && child.family_id) {
        // For draft children, use family_id
        parentKey = child.family_id;
      } else if (child.parent_user_id || child.parent_id) {
        // For registered children, use parent user_id
        parentKey = child.parent_user_id || child.parent_id || '';
      } else {
        return; // Skip children without parent/family
      }
      
      if (!parentMap.has(parentKey)) {
        parentMap.set(parentKey, []);
      }
      const parentChildren = parentMap.get(parentKey);
      if (parentChildren) {
        parentChildren.push(child);
      }
    });
    
    return parentMap;
  }, [users, viewMode]);

  const toggleRoleFilter = (role: string) => {
    setFilters(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role],
    }));
  };

  const clearFilters = () => {
    setFilters({
      quickTab: 'all',
      roles: [],
      status: 'all',
      subscriptionType: null,
      locationId: null,
      registrationDateFrom: null,
      registrationDateTo: null,
      accountStatus: null,
    });
  };

  // Get active filters for display
  const activeFilters = useMemo(() => {
    const filtersList: string[] = [];
    
    // Quick tab filters
    if (filters.quickTab !== 'all') {
      const quickTabLabels: Record<string, string> = {
        new: 'New',
        draft: 'Draft',
        no_subscription: 'No Subscription',
        cancel_saves: 'Cancel Saves',
      };
      if (quickTabLabels[filters.quickTab]) {
        filtersList.push(quickTabLabels[filters.quickTab]);
      }
    }
    
    // Role filters
    if (filters.roles.length > 0) {
      const roleLabels = filters.roles.map(role => {
        const labels: Record<string, string> = {
          owner: 'Owner',
          admin: 'Admin',
          coach: 'Coach',
          student: 'Student',
          reception: 'Reception',
          draft: 'Draft Student',
        };
        return labels[role] || role;
      }).join(', ');
      filtersList.push(roleLabels);
    }
    
    // Status filter
    if (filters.status !== 'all') {
      const statusLabels: Record<string, string> = {
        active: 'Active',
        inactive: 'Inactive',
        draft: 'Draft',
        invited: 'Invited',
        registered: 'Registered',
      };
      if (statusLabels[filters.status]) {
        filtersList.push(statusLabels[filters.status]);
      }
    }
    
    // Date range filters
    if (filters.registrationDateFrom || filters.registrationDateTo) {
      const start = filters.registrationDateFrom 
        ? new Date(filters.registrationDateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) 
        : '';
      const end = filters.registrationDateTo 
        ? new Date(filters.registrationDateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) 
        : '';
      if (start && end) {
        filtersList.push(`${start} - ${end}`);
      } else if (start) {
        filtersList.push(`From ${start}`);
      } else if (end) {
        filtersList.push(`Until ${end}`);
      }
    }
    
    return filtersList;
  }, [filters]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter'] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421] mx-auto" />
          <p className="mt-2 text-[#A9A3A3]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Page Header - matches Billing & Subscriptions styling */}
      <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">Student Management</h1>
            <p className="text-white/80 mt-1">Manage your gym members and draft students</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => navigate('/upload-student-data')}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2 text-sm"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Upload Students</span>
              <span className="sm:hidden">Upload</span>
            </Button>
            <Button
              onClick={() => setDirectInviteModal(true)}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2 text-sm"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Direct Invite</span>
              <span className="sm:hidden">Invite</span>
            </Button>
            <Button
              onClick={() => setShowAddStudentModal(true)}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2 text-sm"
            >
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Add & Enroll Student</span>
              <span className="sm:hidden">Enroll</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content - full height, scrollable */}
      <div className="flex-1 bg-[#111010] overflow-auto min-h-0 py-6">
        {/* View Mode Toggle Buttons */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setViewMode('all')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors text-[#EBFFEE] border ${
              viewMode === 'all'
                ? 'bg-[#9B1421] border-[#9B1421] font-semibold'
                : 'bg-[#221F1F] border-[#504A4A] font-medium'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setViewMode('children')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors text-[#EBFFEE] border ${
              viewMode === 'children'
                ? 'bg-[#9B1421] border-[#9B1421] font-semibold'
                : 'bg-[#221F1F] border-[#504A4A] font-medium'
            }`}
          >
            Children
          </button>
          <button
            onClick={() => setViewMode('families')}
            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors text-[#EBFFEE] border ${
              viewMode === 'families'
                ? 'bg-[#9B1421] border-[#9B1421] font-semibold'
                : 'bg-[#221F1F] border-[#504A4A] font-medium'
            }`}
          >
            Families
          </button>
        </div>

        {/* Quick Filter Tabs */}
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
          <button
            onClick={() => setFilters(prev => ({ ...prev, quickTab: 'all' }))}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors flex-shrink-0 min-w-[120px] text-[#EBFFEE] border ${
              filters.quickTab === 'all'
                ? 'bg-[#9B1421] border-[#9B1421] font-semibold'
                : 'bg-[#221F1F] border-[#504A4A] font-medium'
            }`}
          >
            All Students
          </button>
          <button
            onClick={() => setFilters(prev => ({ ...prev, quickTab: 'new' }))}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors flex-shrink-0 min-w-[120px] text-[#EBFFEE] border ${
              filters.quickTab === 'new'
                ? 'bg-[#9B1421] border-[#9B1421] font-semibold'
                : 'bg-[#221F1F] border-[#504A4A] font-medium'
            }`}
          >
            New Students
          </button>
          <button
            onClick={() => setFilters(prev => ({ ...prev, quickTab: 'draft' }))}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors flex-shrink-0 min-w-[120px] text-[#EBFFEE] border ${
              filters.quickTab === 'draft'
                ? 'bg-[#9B1421] border-[#9B1421] font-semibold'
                : 'bg-[#221F1F] border-[#504A4A] font-medium'
            }`}
          >
            Draft Students
          </button>
          <button
            onClick={() => setFilters(prev => ({ ...prev, quickTab: 'no_subscription' }))}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors flex-shrink-0 min-w-[120px] text-[#EBFFEE] border ${
              filters.quickTab === 'no_subscription'
                ? 'bg-[#9B1421] border-[#9B1421] font-semibold'
                : 'bg-[#221F1F] border-[#504A4A] font-medium'
            }`}
          >
            No Subscription
          </button>
          <button
            onClick={() => setFilters(prev => ({ ...prev, quickTab: 'cancel_saves' }))}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors flex-shrink-0 min-w-[120px] text-[#EBFFEE] border ${
              filters.quickTab === 'cancel_saves'
                ? 'bg-[#9B1421] border-[#9B1421] font-semibold'
                : 'bg-[#221F1F] border-[#504A4A] font-medium'
            }`}
          >
            Cancel Saves
          </button>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A9A3A3] w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 bg-[#363232] border border-[#504A4A] rounded-2xl text-sm sm:text-base text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
              />
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              className={`bg-[#221F1F] hover:bg-[#363232] text-[#EBFFEE] border border-[#504A4A] whitespace-nowrap ${showFilters ? 'ring-2 ring-[#9B1421]' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Role Filter */}
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Roles</label>
                    <div className="flex flex-wrap gap-2">
                      {['owner', 'admin', 'coach', 'student', 'reception', 'draft'].map(role => (
                        <button
                          key={role}
                          onClick={() => toggleRoleFilter(role)}
                          className={`px-3 py-1 rounded text-sm ${
                            filters.roles.includes(role)
                              ? 'bg-[#9B1421] text-[#EBFFEE]'
                              : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
                          }`}
                        >
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ 
                        ...prev, 
                        status: e.target.value as FilterState['status']
                      }))}
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="draft">Draft</option>
                      <option value="invited">Invited</option>
                      <option value="registered">Registered</option>
                    </select>
                  </div>

                  {/* Subscription Filter */}
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Subscription</label>
                    <select
                      value={filters.subscriptionType || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, subscriptionType: e.target.value || null }))}
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                    >
                      <option value="">All Subscriptions</option>
                      {subscriptions.length === 0 ? (
                        <option value="" disabled>No subscriptions found</option>
                      ) : (
                        subscriptions.map(sub => (
                          <option key={sub.id} value={sub.name}>{sub.name}</option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Location Filter */}
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Location</label>
                    <select
                      value={filters.locationId || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, locationId: e.target.value || null }))}
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                    >
                      <option value="">All Locations</option>
                      {locations.length === 0 ? (
                        <option value="" disabled>No locations found</option>
                      ) : (
                        locations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))
                      )}
                    </select>
                  </div>

                  {/* Registration Date From */}
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Registration Date From</label>
                    <input
                      type="date"
                      value={filters.registrationDateFrom || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, registrationDateFrom: e.target.value || null }))}
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                    />
                  </div>

                  {/* Registration Date To */}
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Registration Date To</label>
                    <input
                      type="date"
                      value={filters.registrationDateTo || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, registrationDateTo: e.target.value || null }))}
                      className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE]"
                    />
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={clearFilters}
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE]"
                  >
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="text-sm sm:text-base text-[#EBFFEE]">
            Showing {filteredUsers.length} of {users.length} {filteredUsers.length === 1 ? 'user' : 'users'}
          </div>
          <div className="flex items-center gap-4">
            {activeFilters.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-[#EBFFEE] max-w-[60%]">
                <Filter className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{activeFilters.join(' • ')}</span>
                <button
                  onClick={clearFilters}
                  className="flex-shrink-0 p-1 hover:opacity-70 transition-opacity"
                  title="Clear all filters"
                >
                  <Eraser className="w-4 h-4 text-[#EBFFEE]" />
                </button>
              </div>
            )}
            {filteredUsers.length > 0 && (
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-sm sm:text-base text-[#EBFFEE] hover:opacity-80 transition-opacity font-semibold"
              >
                {selectedUsers.size === filteredUsers.length && filteredUsers.length > 0 ? (
                  <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-[#EBFFEE]" />
                ) : (
                  <Square className="w-4 h-4 sm:w-5 sm:h-5 text-[#EBFFEE]" />
                )}
                <span>
                  {selectedUsers.size === filteredUsers.length && filteredUsers.length > 0 ? 'Deselect All' : 'Select All'}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* User List */}
        <div className="relative min-h-[400px]">
          <div 
            ref={listContainerRef} 
            className="grid gap-4"
          >
            {filteredUsers.length === 0 ? (
              <Card className="bg-[#221F1F] border-[#504A4A]">
                <CardContent className="p-8 text-center">
                  <Users className="w-12 h-12 text-[#A9A3A3] mx-auto mb-4" />
                  <p className="text-[#EBFFEE]">No users found matching your criteria</p>
                </CardContent>
              </Card>
            ) : viewMode === 'children' ? (
              // Children view: Group by family and show as family cards
              Array.from(childrenByFamily.entries()).map(([familyKey, familyChildren]) => {
                const familyLastName = familyChildren[0]?.last_name || 'Unknown Family';
                const isExpanded = expandedFamilies.has(familyKey);
                
                return (
                  <div key={familyKey}>
                    <Card className="bg-[#221F1F] border-[#504A4A]">
                      <CardContent className="!py-3 !px-3 sm:!px-4">
                        <button
                          onClick={() => {
                            const newExpanded = new Set(expandedFamilies);
                            if (isExpanded) {
                              newExpanded.delete(familyKey);
                            } else {
                              newExpanded.add(familyKey);
                            }
                            setExpandedFamilies(newExpanded);
                          }}
                          className="w-full flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <h3 className="text-[#EBFFEE] font-semibold text-sm sm:text-base">
                              {familyLastName} Family
                            </h3>
                            <span className="px-2 py-0.5 bg-[#363232] text-[#A9A3A3] rounded text-xs">
                              {familyChildren.length} {familyChildren.length === 1 ? 'account' : 'accounts'}
                            </span>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-[#A9A3A3]" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#A9A3A3]" />
                          )}
                        </button>
                          {isExpanded && (
                            <div className="mt-3 space-y-2 pl-2 border-l-2 border-[#504A4A]">
                              {familyChildren.map(child => {
                                const childId = child.user_id || child.draft_id || '';
                                const isSelected = selectedUsers.has(childId);
                              
                              return (
                                <Card
                                  key={childId}
                                  className="bg-[#363232] border-[#504A4A] hover:border-[#9B1421] transition-colors cursor-pointer"
                                  onClick={() => handleUserClick(child)}
                                >
                                  <CardContent className="!py-2 !px-3 sm:!px-4 h-[60px] flex items-center">
                                    <div className="flex items-center justify-between gap-2 sm:gap-4 w-full">
                                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#221F1F] flex items-center justify-center flex-shrink-0">
                                          {child.avatar_url ? (
                                            <img src={child.avatar_url} alt="" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover" />
                                          ) : (
                                            <Users className="w-3 h-3 sm:w-4 sm:h-4 text-[#A9A3A3]" />
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <h4 className="text-[#EBFFEE] font-medium truncate text-xs sm:text-sm">
                                            {child.user_name || `${child.first_name || ''} ${child.last_name || ''}`.trim() || 'Unknown'}
                                          </h4>
                                          <div className="flex gap-2 text-xs text-[#A9A3A3] mt-0.5 flex-wrap">
                                            {child.phone && (
                                              <span className="flex items-center gap-1">
                                                <Phone className="w-3 h-3" />
                                                {child.phone}
                                              </span>
                                            )}
                                            {child.subscription_type && (
                                              <span className="text-gray-500">
                                                {child.subscription_type}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleUserSelection(childId);
                                        }}
                                        className="flex-shrink-0 p-1 hover:bg-[#221F1F] rounded transition-colors"
                                      >
                                        {isSelected ? (
                                          <CheckSquare className="w-4 h-4 text-rollops-accent" />
                                        ) : (
                                          <Square className="w-4 h-4 text-[#A9A3A3]" />
                                        )}
                                      </button>
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                );
              })
            ) : (
              // All/Families view: Show family heads with expand/collapse
              filteredUsers.map(user => {
                const userId = user.user_id || user.draft_id || '';
                const userKey = user.draft_id || user.user_id || user.email || '';
                const isSelected = selectedUsers.has(userId);
                const isChild = user.is_child === true;
                
                // Get family key for expand/collapse
                const familyKey = user.is_draft && user.family_id 
                  ? user.family_id 
                  : userId;
                const isExpanded = expandedFamilies.has(familyKey);
                
                // Get children for this family
                const familyChildren = user.is_draft && user.family_id
                  ? allChildrenByParent.get(user.family_id) || []
                  : allChildrenByParent.get(userId) || [];
                
                return (
                <div
                  key={userId}
                  ref={(el) => {
                    if (el) userRowRefs.current.set(userKey, el);
                  }}
                >
                <Card
                  className={`bg-[#221F1F] border-[#504A4A] transition-all hover:border-[#9B1421] ${
                    isSelected ? 'border-[#9B1421] ring-2 ring-[#9B1421]' : ''
                  }`}
                >
                  <CardContent className="!py-3 !px-3 sm:!px-4">
                    <div 
                      className="flex items-center justify-between gap-2 sm:gap-4 w-full cursor-pointer"
                      onClick={() => handleUserClick(user)}
                    >
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#363232] flex items-center justify-center flex-shrink-0">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt="" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover" />
                          ) : (
                            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#A9A3A3]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <h3 className="text-[#EBFFEE] font-semibold truncate text-xs sm:text-sm">
                              {user.user_name || user.email || 'Unknown User'}
                            </h3>
                            {(familyChildren.length > 0 || (user.is_draft && draftChildrenCounts[user.draft_id || ''] > 0)) && (
                              <span className="px-1.5 sm:px-2 py-0.5 bg-[#363232] text-[#A9A3A3] rounded text-xs font-medium flex-shrink-0">
                                +{familyChildren.length || draftChildrenCounts[user.draft_id || ''] || 0}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 sm:gap-3 text-xs text-[#A9A3A3] mt-0.5 sm:mt-1 flex-wrap overflow-hidden">
                            {!isChild && user.email && (
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate max-w-[120px] sm:max-w-none">{user.email}</span>
                              </span>
                            )}
                            {user.phone && (
                              <span className="hidden sm:flex items-center gap-1 flex-shrink-0">
                                <Phone className="w-3 h-3" />
                                {user.phone}
                              </span>
                            )}
                            {user.location_name && (
                              <span className="hidden md:flex items-center gap-1 flex-shrink-0">
                                <MapPin className="w-3 h-3" />
                                {user.location_name}
                              </span>
                            )}
                            {user.subscription_type && (
                              <span className="hidden lg:flex items-center gap-1 flex-shrink-0 text-gray-500">
                                {user.subscription_type}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        {familyChildren.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const newExpanded = new Set(expandedFamilies);
                              if (isExpanded) {
                                newExpanded.delete(familyKey);
                              } else {
                                newExpanded.add(familyKey);
                              }
                              setExpandedFamilies(newExpanded);
                            }}
                            className="flex-shrink-0 p-1 hover:bg-[#363232] rounded transition-colors"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#A9A3A3]" />
                            ) : (
                              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-[#A9A3A3]" />
                            )}
                          </button>
                        )}
                        <div className="text-right hidden sm:block">
                          <span className={`px-2 py-0.5 rounded text-xs capitalize ${
                            user.is_draft
                              ? 'bg-yellow-900 text-yellow-300'
                              : user.is_active
                              ? 'bg-[#363232] text-[#A9A3A3]'
                              : 'bg-[#363232] text-[#A9A3A3]'
                          }`}>
                            {user.is_draft ? getDisplayStatus(user) : (user.role || 'unknown')}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleUserSelection(userId);
                          }}
                          className="flex-shrink-0 p-1 hover:bg-[#363232] rounded transition-colors"
                        >
                          {isSelected ? (
                            <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5 text-rollops-accent" />
                          ) : (
                            <Square className="w-4 h-4 sm:w-5 sm:h-5 text-[#A9A3A3]" />
                          )}
                        </button>
                      </div>
                    </div>
                    {isExpanded && familyChildren.length > 0 && (
                      <div className="mt-3 space-y-2 pl-2 border-l-2 border-[#504A4A]">
                        {familyChildren.map(child => {
                          const childId = child.user_id || child.draft_id || '';
                          const isChildSelected = selectedUsers.has(childId);
                          
                          return (
                            <Card
                              key={childId}
                              className="bg-[#363232] border-[#504A4A] hover:border-[#9B1421] transition-colors cursor-pointer"
                              onClick={() => handleUserClick(child)}
                            >
                              <CardContent className="!py-2 !px-3 sm:!px-4 h-[60px] flex items-center">
                                <div className="flex items-center justify-between gap-2 sm:gap-4 w-full">
                                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#221F1F] flex items-center justify-center flex-shrink-0">
                                      {child.avatar_url ? (
                                        <img src={child.avatar_url} alt="" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover" />
                                      ) : (
                                        <Users className="w-3 h-3 sm:w-4 sm:h-4 text-[#A9A3A3]" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-[#EBFFEE] font-medium truncate text-xs sm:text-sm">
                                        {child.user_name || `${child.first_name || ''} ${child.last_name || ''}`.trim() || 'Unknown'}
                                      </h4>
                                      <div className="flex gap-2 text-xs text-[#A9A3A3] mt-0.5 flex-wrap">
                                        {child.phone && (
                                          <span className="flex items-center gap-1">
                                            <Phone className="w-3 h-3" />
                                            {child.phone}
                                          </span>
                                        )}
                                        {child.subscription_type && (
                                          <span className="text-gray-500">
                                            {child.subscription_type}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleUserSelection(childId);
                                    }}
                                    className="flex-shrink-0 p-1 hover:bg-[#221F1F] rounded transition-colors"
                                  >
                                    {isChildSelected ? (
                                      <CheckSquare className="w-4 h-4 text-rollops-accent" />
                                    ) : (
                                      <Square className="w-4 h-4 text-[#A9A3A3]" />
                                    )}
                                  </button>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                    {isExpanded && familyChildren.length > 0 && (
                      <div className="mt-3 space-y-2 pl-2 border-l-2 border-[#504A4A]">
                        {familyChildren.map(child => {
                          const childId = child.user_id || child.draft_id || '';
                          const isChildSelected = selectedUsers.has(childId);
                          
                          return (
                            <Card
                              key={childId}
                              className="bg-[#363232] border-[#504A4A] hover:border-[#9B1421] transition-colors cursor-pointer"
                              onClick={() => handleUserClick(child)}
                            >
                              <CardContent className="!py-2 !px-3 sm:!px-4 h-[60px] flex items-center">
                                <div className="flex items-center justify-between gap-2 sm:gap-4 w-full">
                                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#221F1F] flex items-center justify-center flex-shrink-0">
                                      {child.avatar_url ? (
                                        <img src={child.avatar_url} alt="" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover" />
                                      ) : (
                                        <Users className="w-3 h-3 sm:w-4 sm:h-4 text-[#A9A3A3]" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-[#EBFFEE] font-medium truncate text-xs sm:text-sm">
                                        {child.user_name || `${child.first_name || ''} ${child.last_name || ''}`.trim() || 'Unknown'}
                                      </h4>
                                      <div className="flex gap-2 text-xs text-[#A9A3A3] mt-0.5 flex-wrap">
                                        {child.phone && (
                                          <span className="flex items-center gap-1">
                                            <Phone className="w-3 h-3" />
                                            {child.phone}
                                          </span>
                                        )}
                                        {child.subscription_type && (
                                          <span className="text-gray-500">
                                            {child.subscription_type}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleUserSelection(childId);
                                    }}
                                    className="flex-shrink-0 p-1 hover:bg-[#221F1F] rounded transition-colors"
                                  >
                                    {isChildSelected ? (
                                      <CheckSquare className="w-4 h-4 text-rollops-accent" />
                                    ) : (
                                      <Square className="w-4 h-4 text-[#A9A3A3]" />
                                    )}
                                  </button>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
                </div>
              );
              })
            )
            }
          </div>
        </div>
      </div>

      {/* Sticky bottom action bar — sits at the bottom of the scroll container when rows are selected */}
      {selectedUsers.size > 0 && (
        <div className="shrink-0 border-t border-white/10 bg-[#111010] shadow-[0_-4px_12px_rgba(0,0,0,0.4)]">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <div className="flex items-center gap-2 text-sm text-[#EBFFEE]">
              <CheckSquare className="w-4 h-4 text-rollops-accent" />
              <span className="font-medium">{selectedUsers.size} selected</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleBulkSendInvites}
                disabled={bulkActionLoading}
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2 text-sm"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">{bulkActionLoading ? 'Sending...' : 'Send Invites'}</span>
                <span className="sm:hidden">{bulkActionLoading ? '...' : 'Invites'}</span>
              </Button>
              <Button
                onClick={handleBulkSendEmail}
                disabled={bulkActionLoading}
                className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2 text-sm"
              >
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Send Email</span>
                <span className="sm:hidden">Email</span>
              </Button>
              <button
                onClick={() => setSelectedUsers(new Set())}
                className="p-2 rounded hover:bg-[#221F1F] text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
                aria-label="Clear selection"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Invite Modal */}
      {directInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-[#EBFFEE]">Direct Invite</h2>
              <button
                onClick={() => {
                  setDirectInviteModal(false);
                  setDirectInviteError(null);
                  setDirectInviteEmail('');
                  setDirectInviteFirstName('');
                  setDirectInviteLastName('');
                  setDirectInvitePhone('');
                }}
                className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-[#A9A3A3] mb-4">Send an invitation to someone new</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={directInviteEmail}
                  onChange={(e) => setDirectInviteEmail(e.target.value)}
                  placeholder="email@example.com"
                  maxLength={254}
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                  First Name (optional)
                </label>
                <input
                  type="text"
                  value={directInviteFirstName}
                  onChange={(e) => setDirectInviteFirstName(e.target.value)}
                  placeholder="First Name"
                  maxLength={30}
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                  Last Name (optional)
                </label>
                <input
                  type="text"
                  value={directInviteLastName}
                  onChange={(e) => setDirectInviteLastName(e.target.value)}
                  placeholder="Last Name"
                  maxLength={30}
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  value={directInvitePhone}
                  onChange={(e) => setDirectInvitePhone(e.target.value)}
                  placeholder="Phone Number"
                  maxLength={20}
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
                />
              </div>
              
              {directInviteError && (
                <div className="text-red-400 text-sm">{directInviteError}</div>
              )}
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => {
                    setDirectInviteModal(false);
                    setDirectInviteError(null);
                    setDirectInviteEmail('');
                    setDirectInviteFirstName('');
                    setDirectInviteLastName('');
                    setDirectInvitePhone('');
                  }}
                  disabled={directInviteLoading}
                  className="flex-1 bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDirectInvite}
                  disabled={directInviteLoading || !directInviteEmail.trim()}
                  className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
                >
                  {directInviteLoading ? 'Sending...' : 'Send Invite'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* User Detail Modal */}
      {selectedUser && showDetail && (() => {
        const currentUser = selectedUser; // TypeScript now knows it's non-null in this scope
        return (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4 overflow-hidden"
            onClick={handleBack}
            aria-hidden="true"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div 
              className="flex flex-col resize overflow-hidden"
              style={{
                width: '90vw',
                height: '85vh',
                minWidth: 'min(600px, 95vw)',
                minHeight: '400px',
                maxWidth: '95vw',
                maxHeight: '90vh'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Scrollable Content - invisible container */}
              <div className="flex-1 overflow-y-auto min-h-0">
                <UserDetailView 
                  user={currentUser}
                  onBack={handleBack}
                  draftChildrenCount={draftChildrenCounts[currentUser.draft_id || ''] || 0}
                onSendInvite={handleSendDraftInvite}
                showToast={showToast}
                onUserUpdated={async () => {
                  // Refetch company users data
                  await companyUsersQuery.refetch();
                }}
                refetchUsers={async () => {
                  await companyUsersQuery.refetch();
                }}
              />
            </div>
          </div>
        </div>
      ); })()}
      
      {/* Create & Enroll Student Modal */}
      {companyId && (
        <AddStudentModal
          isOpen={showAddStudentModal}
          onClose={() => setShowAddStudentModal(false)}
          companyId={companyId}
          onSuccess={async () => {
            await companyUsersQuery.refetch();
          }}
        />
      )}

      {/* Enrollment processing overlay */}
      {enrollmentProcessing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70]">
          <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421] mx-auto" />
            <p className="mt-4 text-[#EBFFEE]">Processing enrollment...</p>
            <p className="mt-1 text-sm text-[#A9A3A3]">Setting up payment and subscription</p>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
}

interface DraftChild {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email?: string | null;
  phone?: string | null;
  subscription_name?: string | null;
}

interface AccountNote {
  id: string;
  note: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
}

interface AttendanceRecord {
  location_name: string | null;
  sport_name: string | null;
  age_bracket_name: string | null;
  checked_in_at: string;
}

interface ClassEnrollmentWithRelations {
  checked_in_at: string;
  calendar_events: {
    locations: { name: string } | null;
    company_sports: { sport_name: string } | null;
    company_age_brackets: { title: string } | null;
  } | null;
}

function UserDetailView({ user, onBack, draftChildrenCount, onSendInvite, showToast, onUserUpdated: _onUserUpdated, refetchUsers }: { user: CompanyUser; onBack: () => void; draftChildrenCount?: number; onSendInvite?: (draftStudentId: string, email: string, hasInviteSent: boolean) => Promise<void>; showToast: (message: string, type: 'success' | 'error' | 'info') => void; onUserUpdated?: () => void; refetchUsers?: () => Promise<void> }) {

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingContractDates, setIsEditingContractDates] = useState(false);
  const [editedEmail, setEditedEmail] = useState(user.email || '');
  const [editedPhone, setEditedPhone] = useState(user.phone || '');
  const [editedContractStartDate, setEditedContractStartDate] = useState(user.contract_start_date || '');
  const [editedContractEndDate, setEditedContractEndDate] = useState(user.contract_end_date || '');
  const [saving, setSaving] = useState(false);
  const [savingContractDates, setSavingContractDates] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftChildren, setDraftChildren] = useState<DraftChild[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [showDraftChildrenSection, setShowDraftChildrenSection] = useState(false);
  const [_subscriptions, setSubscriptions] = useState<Array<{ id: string; name: string }>>([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState<Array<{
    id: string;
    contract_start_date: string | null;
    contract_end_date: string | null;
    billing_account_id: string | null;
    attendance_total: number | null;
    attendance_remaining: number | null;
    company_subscriptions: { 
      id: string; 
      name: string; 
      price: number | null;
      subscription_type: string | null;
      access_type: string | null;
      access_value: number | null;
    } | null;
  }>>([]);
  const [billingAccountNames, setBillingAccountNames] = useState<Record<string, string>>({});
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [_selectedSubscription, _setSelectedSubscription] = useState<string>('');
  const [, _setSendingInvite] = useState(false);
  const [showSubscriptionInviteModal, setShowSubscriptionInviteModal] = useState(false);
  const [showOwnerEnrollmentModal, setShowOwnerEnrollmentModal] = useState(false);
  const [availableSubscriptions, setAvailableSubscriptions] = useState<Array<{
    id: string;
    name: string;
    price: number;
    access_type: string;
    access_value: number | null;
    billing_cycle: string;
  }>>([]);
  const [loadingAvailableSubscriptions, setLoadingAvailableSubscriptions] = useState(false);
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [accountNotes, setAccountNotes] = useState<AccountNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [attendanceData, setAttendanceData] = useState<Array<{ week: string; count: number }>>([]);
  const [crmPulse, setCrmPulse] = useState<{
    lastAttendanceDate: string | null;
    primaryLocation: string | null;
    mostAttendedClass: string | null;
  }>({
    lastAttendanceDate: null,
    primaryLocation: null,
    mostAttendedClass: null,
  });
  const [loadingCrmPulse, setLoadingCrmPulse] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Family lead editing state
  const [showEditFamilyLeadModal, setShowEditFamilyLeadModal] = useState(false);
  const [allFamilyMembers, setAllFamilyMembers] = useState<Array<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone_number: string | null;
    is_family_lead: boolean | null;
  }>>([]);
  const [loadingFamilyMembers, setLoadingFamilyMembers] = useState(false);
  const [updatingFamilyLead, setUpdatingFamilyLead] = useState(false);
  const [selectedNewLeadId, setSelectedNewLeadId] = useState<string | null>(null);
  
  // The gym the admin has selected. This used to be state filled in by
  // loadDetailData from `user_company_relationships … .limit(1)` — an
  // unordered, arbitrary membership row that ignored the selected company
  // entirely. Everything below scopes by it: the waiver query, the
  // subscription list, and two writes (subscription invitations and notes),
  // so for a multi-gym admin all of them could land on the wrong gym.
  const { data: companyId } = useCurrentCompany();
  const permissions = usePermissions();
  
  // Fetch signed waivers
  const { data: signedWaivers, isLoading: loadingWaivers } = useSignedWaivers({
    userId: user.user_id || null,
    companyId,
  });
  
  const handleViewWaiver = async (waiverId: string) => {
    try {
      const { getSignedWaiverUrl } = await import('../../services/damageWaivers');
      const url = await getSignedWaiverUrl(waiverId, 3600);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error loading waiver URL:', error);
      showToast('Failed to load waiver. Please try again.', 'error');
    }
  };

  // Function to load draft children
  const loadDraftChildren = async () => {
    if (!user.is_draft || !user.draft_id) return;
    
    setLoadingChildren(true);
    try {
      // Get the draft record to find family_id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: draftRecord, error: draftRecordError } = await (supabase as any)
        .from('staging_import_members')
        .select('family_id, company_id, email')
        .eq('id', user.draft_id)
        .single();

      if (draftRecordError || !draftRecord?.family_id || draftRecord.family_id === '') {
        setDraftChildren([]);
        setLoadingChildren(false);
        return;
      }

      const familyId = draftRecord.family_id;
      const draftCompanyId = draftRecord.company_id;

      // Query for all family members with this family_id (excluding parent)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: allFamilyMembers, error: childrenError } = await (supabase as any)
        .from('staging_import_members')
        .select('id, first_name, last_name, email, phone_number, subscription_name, user_id, family_id')
        .eq('company_id', draftCompanyId)
        .eq('family_id', familyId)
        .neq('id', user.draft_id) // Exclude the parent record
        .eq('is_archived', false);

      if (childrenError) {
        console.error('Error fetching draft children:', childrenError);
        setDraftChildren([]);
      } else if (allFamilyMembers && allFamilyMembers.length > 0) {
        // Filter to only unregistered children (user_id is null)
        // Include all unregistered family members - they are connected accounts
        interface FamilyMember {
          id: string;
          first_name: string | null;
          last_name: string | null;
          email: string | null;
          phone_number: string | null;
          subscription_name: string | null;
          user_id: string | null;
        }
        const unregisteredChildren = (allFamilyMembers as FamilyMember[]).filter((c) => 
          !c.user_id || c.user_id === null
        );
        
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log(`[loadDraftChildren] Found ${unregisteredChildren.length} connected accounts for family ${familyId}`);
        }
        
        setDraftChildren(unregisteredChildren.map((c) => ({
          id: c.id,
          first_name: c.first_name,
          last_name: c.last_name,
          email: c.email,
          phone: c.phone_number,
          subscription_name: c.subscription_name,
        })));
      } else {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log(`[loadDraftChildren] No family members found for family ${familyId}`);
        }
        setDraftChildren([]);
      }
    } catch (err) {
      console.error('Error loading draft children:', err);
      setDraftChildren([]);
    } finally {
      setLoadingChildren(false);
    }
  };

  // Function to load all family members (for editing family lead)
  const loadAllFamilyMembers = async () => {
    if (!user.is_draft || !user.draft_id || !user.family_id || user.family_id === '') {
      setAllFamilyMembers([]);
      return;
    }
    
    setLoadingFamilyMembers(true);
    try {
      // Get the draft record to find family_id and company_id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: draftRecord, error: draftRecordError } = await (supabase as any)
        .from('staging_import_members')
        .select('family_id, company_id')
        .eq('id', user.draft_id)
        .single();

      if (draftRecordError || !draftRecord?.family_id || draftRecord.family_id === '') {
        setAllFamilyMembers([]);
        setLoadingFamilyMembers(false);
        return;
      }

      const familyId = draftRecord.family_id;
      const draftCompanyId = draftRecord.company_id;

      // Query for ALL family members with this family_id (including the current user)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: allFamilyMembers, error: familyError } = await (supabase as any)
        .from('staging_import_members')
        .select('id, first_name, last_name, email, phone_number, is_family_lead')
        .eq('company_id', draftCompanyId)
        .eq('family_id', familyId)
        .eq('is_archived', false)
        .order('created_at', { ascending: true });

      if (familyError) {
        console.error('Error fetching family members:', familyError);
        showToast('Failed to load family members', 'error');
        setAllFamilyMembers([]);
      } else if (allFamilyMembers && allFamilyMembers.length > 0) {
        setAllFamilyMembers(allFamilyMembers);
        // Set the currently selected lead as the default selection
        const currentLead = allFamilyMembers.find((m: { is_family_lead: boolean | null }) => m.is_family_lead === true);
        if (currentLead) {
          setSelectedNewLeadId(currentLead.id);
        } else {
          // If no lead is set, default to the first member
          setSelectedNewLeadId(allFamilyMembers[0].id);
        }
      } else {
        setAllFamilyMembers([]);
      }
    } catch (err) {
      console.error('Error loading family members:', err);
      showToast('Failed to load family members', 'error');
      setAllFamilyMembers([]);
    } finally {
      setLoadingFamilyMembers(false);
    }
  };

  // Function to update the family lead
  const handleUpdateFamilyLead = async () => {
    if (!user.is_draft || !user.draft_id || !user.family_id || !selectedNewLeadId || !companyId) {
      showToast('Missing required information to update family lead', 'error');
      return;
    }

    setUpdatingFamilyLead(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('update_draft_family_lead', {
        p_family_id: user.family_id,
        p_new_lead_draft_id: selectedNewLeadId,
        p_company_id: companyId,
      });

      if (error) {
        console.error('Error updating family lead:', error);
        showToast(error.message || 'Failed to update family lead', 'error');
        return;
      }

      if (data && data.success) {
        showToast(`Family lead updated to ${data.new_lead_name || 'selected member'}`, 'success');
        setShowEditFamilyLeadModal(false);
        // Refresh the user list to reflect the change
        if (refetchUsers) {
          await refetchUsers();
        }
        // Reload the current user's data
        if (_onUserUpdated) {
          _onUserUpdated();
        }
      } else {
        showToast(data?.error || 'Failed to update family lead', 'error');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update family lead';
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Error updating family lead:', err);
      }
      showToast(errorMessage, 'error');
    } finally {
      setUpdatingFamilyLead(false);
    }
  };

  const loadDetailData = async () => {
    try {
      // Get company ID
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return;

      if (!companyId) return;

      // Get current user's role
      const { data: roleData } = await supabase
        .from('user_company_relationships')
        .select('role_id, system_roles!inner(role)')
        .eq('user_id', currentUser.id)
        .eq('company_id', companyId)
        .single();
      if (roleData && (roleData as Record<string, unknown>).system_roles) {
        const systemRoles = (roleData as Record<string, unknown>).system_roles as Record<string, unknown>;
        setCurrentUserRole(systemRoles.role as string);
      }

      // Load subscriptions
      const { data: subsData } = await supabase
        .from('company_subscriptions')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name', { ascending: true });
      if (subsData) setSubscriptions(subsData);

      // Load active subscriptions for this user from subscription_enrollments
      if (user.user_id && !user.is_draft) {
        setLoadingSubscriptions(true);
        try {
          // Complex join query with nested relations - type inference difficult
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: subscriptionsData, error: subsError } = await (supabase as any)
            .from('subscription_enrollments')
            .select(`
              id,
              status,
              contract_start_date,
              contract_end_date,
              billing_account_id,
              attendance_total,
              attendance_remaining,
              company_subscriptions(
                id,
                name,
                price,
                subscription_type,
                access_type,
                access_value
              )
            `)
            .eq('user_id', user.user_id)
            .eq('company_id', companyId)
            .eq('status', 'active')
            .order('created_at', { ascending: false });

          if (!subsError && subscriptionsData) {
            setActiveSubscriptions(subscriptionsData || []);
            
            // Fetch billing account names for subscriptions with billing_account_id
            interface SubscriptionWithBilling {
              billing_account_id: string | null;
            }
            const billingAccountIds: string[] = (subscriptionsData as SubscriptionWithBilling[])
              .map((sub) => sub.billing_account_id)
              .filter((id: string | null): id is string => !!id && id !== user.user_id);
            
            if (billingAccountIds.length > 0) {
              const uniqueBillingIds: string[] = [...new Set(billingAccountIds)];
              const { data: billingAccounts } = await supabase
                .from('user_profiles')
                .select('id, first_name, last_name')
                .in('id', uniqueBillingIds);
              
              if (billingAccounts) {
                const namesMap: Record<string, string> = {};
                interface BillingAccount {
                  id: string;
                  first_name: string | null;
                  last_name: string | null;
                }
                billingAccounts.forEach((account: BillingAccount) => {
                  const fullName = `${account.first_name || ''} ${account.last_name || ''}`.trim() || 'Unknown';
                  namesMap[account.id] = fullName;
                });
                setBillingAccountNames((prev) => ({ ...prev, ...namesMap }));
              }
            }
            
            // Set contract dates from most recent subscription if available
            if (subscriptionsData.length > 0) {
              const latestSub = subscriptionsData[0];
              setEditedContractStartDate(latestSub.contract_start_date || '');
              setEditedContractEndDate(latestSub.contract_end_date || '');
            }
          }
        } catch (subsErr) {
          console.error('Error loading subscriptions:', subsErr);
        } finally {
          setLoadingSubscriptions(false);
        }
      }

      // Load draft children if this is a draft student (always load if count > 0, or if section is expanded)
      if (user.is_draft && user.draft_id && ((draftChildrenCount || 0) > 0 || showDraftChildrenSection)) {
        await loadDraftChildren();
      }

      // Load account notes (for both registered users and draft students)
      if (user.user_id || (user.is_draft && user.draft_id)) {
        // Dynamic query builder - type inference difficult with conditional chaining
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let notesQuery = (supabase as any)
          .from('account_notes')
          .select('id, note, created_by, created_at')
          .eq('company_id', companyId);
        
        if (user.user_id) {
          notesQuery = notesQuery.eq('user_id', user.user_id).is('draft_id', null);
        } else if (user.is_draft && user.draft_id) {
          notesQuery = notesQuery.eq('draft_id', user.draft_id).is('user_id', null);
        }
        
        const { data: notesData, error: notesError } = await notesQuery.order('created_at', { ascending: false });

        if (!notesError && notesData) {
          // Fetch creator names separately to avoid RLS issues
          interface NoteRecord {
            created_by: string;
          }
          const creatorIds = [...new Set((notesData as NoteRecord[]).map((n) => n.created_by))] as string[];
          const creatorNames: Record<string, string> = {};
          
          if (creatorIds.length > 0) {
            const { data: creatorsData } = await supabase
              .from('user_profiles')
              .select('id, first_name, last_name')
              .in('id', creatorIds);
            
            if (creatorsData) {
              interface Creator {
                id: string;
                first_name: string | null;
                last_name: string | null;
              }
              creatorsData.forEach((c: Creator) => {
                creatorNames[c.id] = `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
              });
            }
          }

          interface NoteWithCreator {
            id: string;
            note: string;
            created_by: string;
            created_at: string;
          }
          setAccountNotes((notesData as NoteWithCreator[]).map((n) => ({
            id: n.id,
            note: n.note,
            created_by: n.created_by,
            created_by_name: creatorNames[n.created_by] || 'Unknown',
            created_at: n.created_at,
          })));
        }
      }

      // Load attendance data for chart (last 12 weeks)
      if (user.user_id && !user.is_draft) {
        const twelveWeeksAgo = new Date();
        twelveWeeksAgo.setDate(twelveWeeksAgo.getDate() - 84);
        
        // Table query - using any for simplicity
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: enrollmentsData } = await (supabase as any)
          .from('class_enrollments')
          .select('checked_in_at')
          .eq('student_id', user.user_id)
          .eq('attended', true)
          .not('checked_in_at', 'is', null)
          .gte('checked_in_at', twelveWeeksAgo.toISOString())
          .order('checked_in_at', { ascending: true });

        if (enrollmentsData) {
          // Group by week
          const weeklyCounts: Record<string, number> = {};
          interface EnrollmentRecord {
            checked_in_at: string;
          }
          (enrollmentsData as EnrollmentRecord[]).forEach((e) => {
            const date = new Date(e.checked_in_at);
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
            const weekKey = weekStart.toISOString().split('T')[0];
            weeklyCounts[weekKey] = (weeklyCounts[weekKey] || 0) + 1;
          });

          const weeks = Object.keys(weeklyCounts)
            .sort()
            .slice(-12)
            .map(week => ({
              week: new Date(week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
              count: weeklyCounts[week],
            }));

          setAttendanceData(weeks);
        }
        
        // Load CRM Pulse data
        await loadCRMPulseData(user.user_id);
      }
    } catch (err) {
      console.error('Error loading detail data:', err);
    }
  };

  const loadCRMPulseData = async (userId: string) => {
    setLoadingCrmPulse(true);
    try {
      // Calculate last attendance date from class_enrollments filtered by company (company-specific)
      // Use optimized view for location and class data
      let attendanceData: AttendanceRecord[] = [];
      
      try {
        // View not in generated types - user_attendance_detail is a database view
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (supabase as any)
          .from('user_attendance_detail')
          .select('location_name, sport_name, age_bracket_name, checked_in_at')
          .eq('student_id', userId)
          .eq('attended', true)
          .not('checked_in_at', 'is', null)
          .order('checked_in_at', { ascending: false })
          .limit(100);
        attendanceData = (result.data || []) as AttendanceRecord[];
        // Note: result.error is available but not currently used
      } catch (err) {
        console.warn('[UserDetails] Attendance view not available, using direct query:', err);
        // Fallback to direct query if view doesn't exist
        // Complex join query - type inference difficult, using any for Supabase query builder
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: enrollments, error: enrollError } = await (supabase as any)
          .from('class_enrollments')
          .select(`
            checked_in_at,
            calendar_events!inner(
              locations(name),
              company_sports(sport_name),
              company_age_brackets(title)
            )
          `)
          .eq('student_id', userId)
          .eq('attended', true)
          .not('checked_in_at', 'is', null)
          .order('checked_in_at', { ascending: false })
          .limit(100);
        
        if (!enrollError && enrollments) {
          attendanceData = (enrollments as ClassEnrollmentWithRelations[]).map((e) => ({
            location_name: e.calendar_events?.locations?.name || null,
            sport_name: e.calendar_events?.company_sports?.sport_name || null,
            age_bracket_name: e.calendar_events?.company_age_brackets?.title || null,
            checked_in_at: e.checked_in_at,
          }));
        }
      }

      // Get last attendance date from the most recent attendance record (company-specific)
      let lastAttendanceDate: string | null = null;
      if (attendanceData && attendanceData.length > 0) {
        lastAttendanceDate = attendanceData[0].checked_in_at;
      }

      let primaryLocation: string | null = null;
      let mostAttendedClass: string | null = null;

      if (attendanceData && attendanceData.length > 0) {
        // Count locations
        const locationCounts: Record<string, number> = {};
        attendanceData.forEach((record) => {
          if (record.location_name) {
            locationCounts[record.location_name] = (locationCounts[record.location_name] || 0) + 1;
          }
        });
        const sortedLocations = Object.entries(locationCounts).sort((a, b) => b[1] - a[1]);
        primaryLocation = sortedLocations[0]?.[0] || null;

        // Count classes (sport + age bracket)
        const classCounts: Record<string, number> = {};
        attendanceData.forEach((record) => {
          const sport = record.sport_name;
          const ageBracket = record.age_bracket_name;
          if (sport && ageBracket) {
            const classKey = `${sport} - ${ageBracket}`;
            classCounts[classKey] = (classCounts[classKey] || 0) + 1;
          } else if (sport) {
            classCounts[sport] = (classCounts[sport] || 0) + 1;
          }
        });
        const sortedClasses = Object.entries(classCounts).sort((a, b) => b[1] - a[1]);
        mostAttendedClass = sortedClasses[0]?.[0] || null;
      }

      setCrmPulse({
        lastAttendanceDate,
        primaryLocation,
        mostAttendedClass,
      });
    } catch (err) {
      console.error('Error loading CRM pulse data:', err);
    } finally {
      setLoadingCrmPulse(false);
    }
  };

  useEffect(() => {
    loadDetailData();
    // Auto-expand Connected Accounts section if accounts exist
    if ((draftChildrenCount || 0) > 0) {
      setShowDraftChildrenSection(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.user_id, user.draft_id, draftChildrenCount]);

  const handleSaveContractDates = async () => {
    if (!user.user_id) {
      setError('Cannot save contract dates: user ID not found');
      return;
    }

    if (activeSubscriptions.length === 0) {
      setError('Cannot save contract dates: no active subscription found');
      return;
    }

    try {
      setSavingContractDates(true);
      setError(null);

      // Get the most recent active subscription
      const latestSub = activeSubscriptions[0];
      interface SubscriptionUpdateData {
        updated_at: string;
        contract_start_date: string | null;
        contract_end_date: string | null;
      }
      const updateData: SubscriptionUpdateData = {
        updated_at: new Date().toISOString(),
        contract_start_date: editedContractStartDate || null,
        contract_end_date: editedContractEndDate || null,
      };

      // Update subscription enrollment
      // NOTE: We ONLY update subscription_enrollments, NOT user_profiles
      // Contract dates are company-specific and belong in subscription_enrollments
      // Users can belong to multiple companies with different contract dates
      // Table update - using any for Supabase query builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: updateError } = await (supabase as any)
        .from('subscription_enrollments')
        .update(updateData)
        .eq('id', latestSub.id);

      if (updateError) throw updateError;

      // Reload subscriptions to refresh display
      await loadDetailData();
      setIsEditingContractDates(false);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update contract dates';
      setError(errorMessage);
    } finally {
      setSavingContractDates(false);
    }
  };

  const loadAvailableSubscriptions = async () => {
    setLoadingAvailableSubscriptions(true);
    try {
      // The getUser() round trip that used to stand here only existed to feed
      // the membership lookup below it; `companyId` is already null when there
      // is no session, so the guard is the same one.
      if (!companyId) return;

      // Table query - using any for simplicity
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: subsData, error: subsError } = await (supabase as any)
        .from('company_subscriptions')
        .select('id, name, price, access_type, access_value, billing_cycle')
        .eq('company_id', companyId)
        .order('name', { ascending: true });

      if (subsError) throw subsError;
      setAvailableSubscriptions((subsData || []) as Array<{
        id: string;
        name: string;
        price: number;
        access_type: string;
        access_value: number | null;
        billing_cycle: string;
      }>);
    } catch (err) {
      console.error('Error loading available subscriptions:', err);
    } finally {
      setLoadingAvailableSubscriptions(false);
    }
  };

  const handleSendSubscriptionInvitation = async (subscriptionId: string) => {
    if (!user.user_id) {
      setError('Cannot send invitation: user ID not found');
      return;
    }

    setSendingInvitation(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated');

      if (!companyId) throw new Error('Company not found');

      interface SubscriptionInvitationData {
        subscription_id: string;
        company_id: string;
        invited_by: string;
        user_id: string;
        status: string;
      }
      const invitationData: SubscriptionInvitationData = {
        subscription_id: subscriptionId,
        company_id: companyId,
        invited_by: currentUser.id,
        user_id: user.user_id,
        status: 'pending',
      };

      // Table insert - using any for Supabase query builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('subscription_invitations')
        .insert(invitationData);

      if (error) throw error;

      showToast('Subscription invitation sent successfully!', 'success');
      setShowSubscriptionInviteModal(false);
      // Reload user details to refresh subscription status
      await loadDetailData();
    } catch (err: unknown) {
      showToast(getFriendlyErrorMessage(err, 'invite'), 'error');
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleSave = async () => {
    if (!user.is_draft || !user.draft_id) {
      setError('Can only edit draft students');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Table update - using any for Supabase query builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: updateError } = await (supabase as any)
        .from('staging_import_members')
        .update({
          email: editedEmail || null,
          phone_number: editedPhone || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.draft_id);

      if (updateError) throw updateError;

      setIsEditing(false);
      window.location.reload();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update draft student';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Removed unused function - was never called
  // const handleSendInvite = async () => {
  //   ... function body removed ...
  // };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    if (!user.user_id && !(user.is_draft && user.draft_id)) {
      setError('Cannot add note: user has no user_id or draft_id');
      return;
    }

    try {
      setAddingNote(true);
      setError(null);

      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated');

      if (!companyId) throw new Error('Company not found');

      interface NoteInsertData {
        company_id: string;
        created_by: string;
        note: string;
        user_id?: string | null;
        draft_id?: string | null;
      }
      const noteData: NoteInsertData = {
        company_id: companyId,
        created_by: currentUser.id,
        note: newNote.trim(),
      };

      // Link to user_id if registered, or draft_id if draft student
      if (user.user_id) {
        noteData.user_id = user.user_id;
        noteData.draft_id = null;
      } else if (user.is_draft && user.draft_id) {
        noteData.draft_id = user.draft_id;
        noteData.user_id = null;
      } else {
        throw new Error('Cannot add note: user has no user_id or draft_id');
      }

      // Table insert - using any for Supabase query builder
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: noteError } = await (supabase as any)
        .from('account_notes')
        .insert(noteData);

      if (noteError) throw noteError;

      setNewNote('');
      await loadDetailData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add note';
      setError(errorMessage);
    } finally {
      setAddingNote(false);
    }
  };

  const maxAttendance = Math.max(...attendanceData.map(d => d.count), 1);

  return (
    <div className="space-y-6">
      <Card className="bg-[#221F1F] border-[#504A4A]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={onBack}
                className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] p-2 border border-[#504A4A]"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <CardTitle className="text-[#EBFFEE]">User Details</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {/* Settings cog - visible to owner/admin only */}
              {(permissions.role === 'owner' || permissions.role === 'admin' || permissions.isAdmin || permissions.isDeveloper) && (
                <Button
                  onClick={() => setShowSettingsModal(true)}
                  className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] p-2 border border-[#504A4A]"
                  title="Edit user settings (role, status, contract dates)"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              )}
              {/* Resend invite button - visible to coach/reception too */}
              {user.is_draft && onSendInvite && user.draft_id && (user.email || user.draft_email) && (
                <Button
                  onClick={() => {
                    if (!user.draft_id) return;
                    const hasInviteSent = user.status === 'invited' || !!user.invite_sent_at;
                    onSendInvite(user.draft_id, user.email || user.draft_email || '', hasInviteSent);
                  }}
                  className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {user.status === 'invited' || user.invite_sent_at ? 'Re-send Invite' : 'Send Invite'}
                </Button>
              )}
              {/* Edit button for draft users - visible to coach/reception too */}
              {user.is_draft && (
                <Button
                  onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                  disabled={saving}
                  className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center gap-2"
                >
                  <Pencil className="w-4 h-4" />
                  {saving ? 'Saving...' : isEditing ? 'Save' : 'Edit'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-900/30 border border-red-600/50 text-text-primary p-3 rounded">
              {error}
            </div>
          )}

          {/* Avatar and Basic Info */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#363232] flex items-center justify-center flex-shrink-0">
              {user.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover" />
              ) : (
                <Users className="w-10 h-10 sm:w-12 sm:h-12 text-[#A9A3A3]" />
              )}
            </div>
            <div className="flex-1 text-center sm:text-left min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-[#EBFFEE] mb-2 truncate">
                {user.is_draft
                  ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User'
                  : user.user_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User'
                }
              </h2>
              <div className="flex flex-wrap justify-center sm:justify-start gap-x-4 gap-y-1 text-sm text-[#A9A3A3]">
                {user.email && (
                  <span className="flex items-center gap-1 truncate max-w-full">
                    <Mail className="w-4 h-4 shrink-0" />
                    <span className="truncate">{user.email}</span>
                  </span>
                )}
                {user.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4 shrink-0" />
                    {user.phone}
                  </span>
                )}
                {user.location_name && (
                  <span className="flex items-center gap-1 truncate max-w-full">
                    <MapPin className="w-4 h-4 shrink-0" />
                    <span className="truncate">{user.location_name}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col items-center sm:items-end gap-2 sm:text-right">
              <span className={`px-3 py-1 rounded text-sm capitalize ${
                user.is_draft
                  ? 'bg-yellow-900 text-yellow-300'
                  : user.is_active
                  ? 'bg-[#363232] text-[#EBFFEE]'
                  : 'bg-[#363232] text-[#A9A3A3]'
              }`}>
                {user.is_draft ? getDisplayStatus(user) : (user.role || 'unknown')}
              </span>
              {/* Edit Family Lead button - only show for draft users with family_id */}
              {user.is_draft && user.family_id && user.family_id !== '' && (currentUserRole === 'owner' || currentUserRole === 'admin') && (
                <button
                  onClick={async () => {
                    setShowEditFamilyLeadModal(true);
                    await loadAllFamilyMembers();
                  }}
                  className="px-3 py-1.5 text-sm bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] rounded border border-[#504A4A] hover:border-[#9B1421] transition-colors flex items-center gap-2"
                  title="Edit Family Lead"
                >
                  <Users className="w-4 h-4" />
                  Edit Family Lead
                </button>
              )}
            </div>
          </div>

          {/* Basic Information Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-[#A9A3A3]">Role</label>
              <p className="text-[#EBFFEE]">{user.role || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm text-[#A9A3A3]">Status</label>
              <p className="text-[#EBFFEE] capitalize">{getDisplayStatus(user)}</p>
            </div>
            {user.subscription_type && (
              <div>
                <label className="text-sm text-[#A9A3A3]">Subscription</label>
                <p className="text-[#EBFFEE]">{user.subscription_type}</p>
              </div>
            )}
            {user.created_at && (
              <div>
                <label className="text-sm text-[#A9A3A3]">Created At</label>
                <p className="text-[#EBFFEE]">{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
            )}
            {user.original_start_date && (
              <div>
                <label className="text-sm text-[#A9A3A3]">Original Start Date</label>
                <p className="text-[#EBFFEE]">{new Date(user.original_start_date).toLocaleDateString()}</p>
              </div>
            )}
            {user.last_attendance_date && (
              <div>
                <label className="text-sm text-[#A9A3A3]">Last Attendance</label>
                <p className="text-[#EBFFEE]">{new Date(user.last_attendance_date).toLocaleDateString()}</p>
              </div>
            )}
            {!user.last_attendance_date && !user.is_draft && (
              <div>
                <label className="text-sm text-[#A9A3A3]">Last Attendance</label>
                <p className="text-orange-400">Never attended</p>
              </div>
            )}
            {/* Contract Dates */}
            {(editedContractStartDate || editedContractEndDate || (currentUserRole === 'owner' || currentUserRole === 'admin')) && (
              <>
                <div>
                  <label className="text-sm text-[#A9A3A3]">Contract Start Date</label>
                  {isEditingContractDates && (currentUserRole === 'owner' || currentUserRole === 'admin') ? (
                    <input
                      type="date"
                      value={editedContractStartDate}
                      onChange={(e) => setEditedContractStartDate(e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                    />
                  ) : (
                    <p className="text-[#EBFFEE]">{editedContractStartDate ? new Date(editedContractStartDate).toLocaleDateString() : 'Not set'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-[#A9A3A3]">Contract End Date</label>
                  {isEditingContractDates && (currentUserRole === 'owner' || currentUserRole === 'admin') ? (
                    <input
                      type="date"
                      value={editedContractEndDate}
                      onChange={(e) => setEditedContractEndDate(e.target.value)}
                      className="w-full mt-1 px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                    />
                  ) : (
                    <p className="text-[#EBFFEE]">{editedContractEndDate ? new Date(editedContractEndDate).toLocaleDateString() : 'Not set'}</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Subscription Details Section */}
          {!user.is_draft && (currentUserRole === 'owner' || currentUserRole === 'admin') && (
            <div className="pt-4 border-t border-[#504A4A]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#EBFFEE]">Subscription Details</h3>
                {activeSubscriptions.length > 0 && (
                  <button
                    onClick={() => {
                      const latestSub = activeSubscriptions[0];
                      setEditedContractStartDate(latestSub.contract_start_date || '');
                      setEditedContractEndDate(latestSub.contract_end_date || '');
                      setError(null);
                      setIsEditingContractDates(true);
                    }}
                    className="p-2 hover:bg-[#363232] rounded transition-colors"
                    title="Edit Contract Dates"
                  >
                    <Pencil className="w-5 h-5 text-rollops-accent" />
                  </button>
                )}
              </div>
              
              {loadingSubscriptions ? (
                <p className="text-[#A9A3A3]">Loading subscriptions...</p>
              ) : activeSubscriptions.length > 0 ? (
                <div className="space-y-3">
                  {activeSubscriptions.map((sub) => (
                    <div key={sub.id} className="bg-[#363232] rounded-lg p-4">
                      <p className="text-[#EBFFEE] font-semibold mb-2">
                        {sub.company_subscriptions?.name || 'Subscription'}
                      </p>
                      {sub.company_subscriptions?.price && (
                        <p className="text-sm text-[#A9A3A3] mb-1">
                          ${Number(sub.company_subscriptions.price).toFixed(2)} / month
                        </p>
                      )}
                      {/* Show attendance remaining/total if this subscription has limits */}
                      {sub.company_subscriptions?.subscription_type === 'punch_card' && sub.company_subscriptions?.access_value && (
                        <p className="text-sm text-rollops-accent font-medium mb-1">
                          {(() => {
                            const remaining = sub.attendance_remaining || 0;
                            const total = sub.company_subscriptions.access_value;
                            const unit = sub.company_subscriptions.access_type === 'classes' ? 'punches' : 'days';
                            return `${remaining} of ${total} ${unit} remaining`;
                          })()}
                        </p>
                      )}
                      {/* Show monthly attendance remaining/total for regular subscriptions */}
                      {sub.company_subscriptions?.subscription_type === 'recurring' && sub.company_subscriptions?.access_value && sub.company_subscriptions?.access_type !== 'unlimited' && (
                        <p className="text-sm text-rollops-accent font-medium mb-1">
                          {(() => {
                            const remaining = sub.attendance_remaining || 0;
                            const total = sub.company_subscriptions.access_value;
                            const unit = sub.company_subscriptions.access_type === 'classes' ? 'classes' : 'days';
                            return `${remaining} of ${total} ${unit} remaining this month`;
                          })()}
                        </p>
                      )}
                      <p className="text-sm text-[#A9A3A3]">
                        {sub.contract_start_date ? new Date(sub.contract_start_date).toLocaleDateString() : 'Not set'} - {sub.contract_end_date ? new Date(sub.contract_end_date).toLocaleDateString() : 'Not set'}
                      </p>
                      {sub.billing_account_id && sub.billing_account_id !== user.user_id && billingAccountNames[sub.billing_account_id] && (
                        <p className="text-sm text-[#A9A3A3] italic mt-1">
                          Billed to: {billingAccountNames[sub.billing_account_id]}
                        </p>
                      )}
                    </div>
                  ))}
                  
                  {isEditingContractDates && (
                    <div className="bg-[#363232] rounded-lg p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Contract Start Date</label>
                        <input
                          type="date"
                          value={editedContractStartDate}
                          onChange={(e) => setEditedContractStartDate(e.target.value)}
                          className="w-full px-3 py-2 bg-[#221F1F] border border-[#504A4A] rounded text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Contract End Date</label>
                        <input
                          type="date"
                          value={editedContractEndDate}
                          onChange={(e) => setEditedContractEndDate(e.target.value)}
                          className="w-full px-3 py-2 bg-[#221F1F] border border-[#504A4A] rounded text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                        />
                      </div>
                      {error && <p className="text-red-400 text-sm">{error}</p>}
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSaveContractDates}
                          disabled={savingContractDates}
                          className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
                        >
                          {savingContractDates ? 'Saving...' : 'Save Contract Dates'}
                        </Button>
                        <Button
                          onClick={() => {
                            setIsEditingContractDates(false);
                            setError(null);
                            // Reset to original values
                            if (activeSubscriptions.length > 0) {
                              const latestSub = activeSubscriptions[0];
                              setEditedContractStartDate(latestSub.contract_start_date || '');
                              setEditedContractEndDate(latestSub.contract_end_date || '');
                            }
                          }}
                          className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => {
                      setShowSubscriptionInviteModal(true);
                      loadAvailableSubscriptions();
                    }}
                    className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    Invite to Subscription
                  </Button>
                  <Button
                    onClick={() => setShowOwnerEnrollmentModal(true)}
                    className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] flex items-center gap-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    Enroll & Setup Billing
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Draft Student Editing */}
          {user.is_draft && isEditing && (
            <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-[#504A4A]">
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Email</label>
                <input
                  type="email"
                  value={editedEmail}
                  onChange={(e) => setEditedEmail(e.target.value)}
                  maxLength={254}
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">Phone</label>
                <input
                  type="tel"
                  value={editedPhone}
                  onChange={(e) => setEditedPhone(e.target.value)}
                  maxLength={20}
                  className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:border-[#9B1421]"
                />
              </div>
              <div className="flex gap-2 justify-end md:col-span-2">
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedEmail(user.email || '');
                    setEditedPhone(user.phone || '');
                    setError(null);
                  }}
                  className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Draft Children Section */}
          {user.is_draft && (draftChildrenCount || 0) > 0 && (
            <div className="pt-4 border-t border-[#504A4A]">
              {/* Count Card - Always visible */}
              <div className="bg-[#363232] rounded-lg p-4 border border-[#504A4A] mb-4">
                <button
                  onClick={async () => {
                    const willExpand = !showDraftChildrenSection;
                    setShowDraftChildrenSection(willExpand);
                    // Load children when expanding if not already loaded
                    if (willExpand && draftChildren.length === 0 && !loadingChildren) {
                      await loadDraftChildren();
                    }
                  }}
                  className="w-full flex items-center justify-between hover:opacity-80 transition-opacity"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-[#9B1421] rounded-full p-2">
                      <Users className="w-5 h-5 text-[#EBFFEE]" />
                    </div>
                    <div className="text-left">
                      <p className="text-[#EBFFEE] font-medium">
                        {user.is_draft ? 'Draft Connected Accounts' : 'Connected Accounts'}
                      </p>
                      <p className="text-sm text-[#A9A3A3]">
                        {draftChildren.length > 0 ? draftChildren.length : draftChildrenCount || 0} 
                        {' '}{(draftChildren.length > 0 ? draftChildren.length : draftChildrenCount || 0) === 1 ? 'account' : 'accounts'} connected
                      </p>
                    </div>
                  </div>
                  {showDraftChildrenSection ? (
                    <ChevronUp className="w-5 h-5 text-[#A9A3A3]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#A9A3A3]" />
                  )}
                </button>
              </div>
              
              {/* Expanded Children List */}
              {showDraftChildrenSection && (
                loadingChildren ? (
                  <p className="text-[#A9A3A3]">Loading children...</p>
                ) : draftChildren.length > 0 ? (
                  <div className="space-y-3">
                    {draftChildren.map((child) => (
                      <Card key={child.id} className="bg-[#363232] border-[#504A4A] hover:border-[#9B1421] transition-colors">
                        <CardContent className="!py-3 !px-4 !pt-4 min-h-[72px] flex items-center">
                          <div className="grid md:grid-cols-2 gap-4 w-full">
                            <div className="flex flex-col justify-center">
                              <p className="text-[#EBFFEE] font-medium">
                                {child.first_name || ''} {child.last_name || ''}
                                {!child.first_name && !child.last_name && <span className="text-[#A9A3A3] italic">(No name)</span>}
                              </p>
                              {child.email && <p className="text-sm text-[#A9A3A3] flex items-center gap-1"><Mail className="w-3 h-3" />{child.email}</p>}
                              {child.phone && <p className="text-sm text-[#A9A3A3] flex items-center gap-1"><Phone className="w-3 h-3" />{child.phone}</p>}
                            </div>
                            <div className="flex items-center justify-end">
                              {child.subscription_name && (
                                <p className="text-sm text-[#A9A3A3]">Subscription: <span className="text-[#EBFFEE]">{child.subscription_name}</span></p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#A9A3A3]">
                    {loadingChildren ? 'Loading connected accounts...' : 'No connected accounts found.'}
                  </p>
                )
              )}
            </div>
          )}

          {/* Quick Pulse Section */}
          {!user.is_draft && user.user_id && (
            <div className="pt-4 border-t border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Quick Pulse
              </h3>
              {loadingCrmPulse ? (
                <p className="text-[#A9A3A3]">Loading...</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-[#363232] rounded-lg p-4 text-center">
                    <Calendar className="w-6 h-6 text-rollops-accent mx-auto mb-2" />
                    <p className="text-sm text-[#A9A3A3] mb-1">Last Attendance</p>
                    <p className="text-[#EBFFEE] font-medium">
                      {crmPulse.lastAttendanceDate
                        ? new Date(crmPulse.lastAttendanceDate).toLocaleDateString()
                        : 'Never'}
                    </p>
                  </div>
                  <div className="bg-[#363232] rounded-lg p-4 text-center">
                    <MapPin className="w-6 h-6 text-rollops-accent mx-auto mb-2" />
                    <p className="text-sm text-[#A9A3A3] mb-1">Primary Location</p>
                    <p className="text-[#EBFFEE] font-medium truncate" title={crmPulse.primaryLocation || 'N/A'}>
                      {crmPulse.primaryLocation || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-[#363232] rounded-lg p-4 text-center">
                    <Activity className="w-6 h-6 text-rollops-accent mx-auto mb-2" />
                    <p className="text-sm text-[#A9A3A3] mb-1">Most Attended</p>
                    <p className="text-[#EBFFEE] font-medium line-clamp-2" title={crmPulse.mostAttendedClass || 'N/A'}>
                      {crmPulse.mostAttendedClass || 'N/A'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Attendance Chart */}
          {!user.is_draft && attendanceData.length > 0 && (
            <div className="pt-4 border-t border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Attendance (Last 12 Weeks)
              </h3>
              <div className="bg-[#363232] rounded-lg p-4">
                <div className="flex items-end gap-1 h-48">
                  {attendanceData.map((data, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-[#9B1421] rounded-t relative group" style={{ height: `${(data.count / maxAttendance) * 100}%`, minHeight: data.count > 0 ? '4px' : '0' }}>
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs text-[#EBFFEE] font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {data.count} class{data.count !== 1 ? 'es' : ''}
                        </div>
                      </div>
                      <div className="text-xs text-[#A9A3A3] mt-1 truncate w-full text-center" title={data.week}>
                        {data.week.split(' ')[0]}
                      </div>
                    </div>
                  ))}
                </div>
                {attendanceData.length === 0 && (
                  <p className="text-[#A9A3A3] text-center py-8">No attendance data available</p>
                )}
              </div>
            </div>
          )}

          {/* Account Notes - Show for both registered users and draft students */}
          {(user.user_id || (user.is_draft && user.draft_id)) && (
            <div className="pt-4 border-t border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Account Notes
              </h3>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note about this account..."
                    className="flex-1 px-3 py-2 bg-[#363232] border border-[#504A4A] rounded text-[#EBFFEE] placeholder-[#A9A3A3] resize-none focus:outline-none focus:border-[#9B1421]"
                    rows={3}
                  />
                  <Button
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || addingNote}
                    className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] self-start"
                  >
                    {addingNote ? 'Adding...' : <><Plus className="w-4 h-4 mr-2" />Add Note</>}
                  </Button>
                </div>
                <div className="space-y-3">
                  {accountNotes.length === 0 ? (
                    <p className="text-[#A9A3A3] text-sm">No notes yet</p>
                  ) : (
                    accountNotes.map((note) => (
                      <Card key={note.id} className="bg-[#363232] border-[#504A4A]">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <p className="text-sm text-[#A9A3A3]">
                              {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                            </p>
                          </div>
                          <p className="text-[#EBFFEE]">{note.note}</p>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Signed Waivers - Show for registered users */}
          {user.user_id && !user.is_draft && (
            <div className="pt-4 border-t border-[#504A4A]">
              <h3 className="text-lg font-semibold text-[#EBFFEE] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Signed Waivers
              </h3>
              {loadingWaivers ? (
                <p className="text-[#A9A3A3] text-sm">Loading waivers...</p>
              ) : !signedWaivers || signedWaivers.length === 0 ? (
                <p className="text-[#A9A3A3] text-sm">No signed waivers found</p>
              ) : (
                <div className="space-y-2">
                  {signedWaivers.map((waiver) => (
                    <Card 
                      key={waiver.id} 
                      className="bg-[#363232] border-[#504A4A]"
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-[#EBFFEE] font-medium">{waiver.signed_by}</p>
                            <p className="text-[#A9A3A3] text-sm">
                              {new Date(waiver.signed_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            onClick={() => handleViewWaiver(waiver.id)}
                            className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
                          >
                            View
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Subscription Invite Modal */}
      {showSubscriptionInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[#EBFFEE]">Select Subscription to Invite</h3>
              <button
                onClick={() => setShowSubscriptionInviteModal(false)}
                className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            {loadingAvailableSubscriptions ? (
              <p className="text-[#A9A3A3] text-center py-8">Loading subscriptions...</p>
            ) : availableSubscriptions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#A9A3A3]">No subscriptions available.</p>
                <p className="text-sm text-[#A9A3A3] mt-2">Create a subscription type first.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {availableSubscriptions.map((sub) => {
                  const accessDisplay = sub.access_type === 'unlimited' 
                    ? 'Unlimited' 
                    : `${sub.access_value} ${sub.access_type}`;
                  const billingCycle = sub.billing_cycle === 'monthly' ? 'Monthly' :
                    sub.billing_cycle === 'quarterly' ? 'Quarterly' :
                    sub.billing_cycle === 'annual' ? 'Annual' :
                    sub.billing_cycle === 'biyearly' ? 'Bi-yearly' : sub.billing_cycle || 'Monthly';
                  
                  return (
                    <button
                      key={sub.id}
                      onClick={() => handleSendSubscriptionInvitation(sub.id)}
                      disabled={sendingInvitation}
                      className="w-full bg-[#363232] hover:bg-[#504A4A] border border-[#504A4A] rounded-lg p-4 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-[#EBFFEE] font-semibold">
                            {sub.name}
                          </p>
                          <p className="text-[#A9A3A3] text-sm mt-1">
                            ${Number(sub.price).toFixed(2)} / {billingCycle}
                          </p>
                          <p className="text-[#A9A3A3] text-xs mt-1">
                            {accessDisplay}
                          </p>
                        </div>
                        {sendingInvitation ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#9B1421]" />
                        ) : (
                          <span className="text-[#A9A3A3]">&gt;</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Owner Enrollment Modal */}
      {user.user_id && user.company_id && (
        <OwnerEnrollmentModal
          isOpen={showOwnerEnrollmentModal}
          onClose={() => setShowOwnerEnrollmentModal(false)}
          student={{
            user_id: user.user_id,
            company_id: user.company_id,
            first_name: user.first_name,
            last_name: user.last_name,
          }}
          onSuccess={async () => {
            if (refetchUsers) await refetchUsers();
          }}
        />
      )}

      {/* User Settings Modal */}
      {user.user_id && user.company_id && refetchUsers && (
        <UserSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          user={{
            user_id: user.user_id,
            company_id: user.company_id,
            role: user.role || user.role_key || 'student', // Use role_key as fallback
            is_active: user.is_active,
          }}
          onSuccess={async () => {
            // Reload user data
            loadDetailData();
            // Refresh the user list - TanStack Query will automatically refetch due to invalidation
            // But we also manually refetch to ensure immediate update
            await refetchUsers();
            showToast('User settings updated successfully', 'success');
          }}
        />
      )}

      {/* Edit Family Lead Modal */}
      {showEditFamilyLeadModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70]" onClick={() => setShowEditFamilyLeadModal(false)}>
          <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#EBFFEE] flex items-center gap-2">
                <Users className="w-5 h-5" />
                Edit Family Lead
              </h3>
              <button
                onClick={() => setShowEditFamilyLeadModal(false)}
                className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-[#A9A3A3] mb-4">
              Select which family member should be marked as the family lead. This determines which member is displayed as the primary account in the account manager.
            </p>

            {loadingFamilyMembers ? (
              <p className="text-[#A9A3A3] text-center py-8">Loading family members...</p>
            ) : allFamilyMembers.length === 0 ? (
              <p className="text-[#A9A3A3] text-center py-8">No family members found</p>
            ) : (
              <>
                <div className="space-y-2 mb-6">
                  {allFamilyMembers.map((member) => {
                    const isSelected = selectedNewLeadId === member.id;
                    const isCurrentLead = member.is_family_lead === true;
                    return (
                      <button
                        key={member.id}
                        onClick={() => setSelectedNewLeadId(member.id)}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          isSelected
                            ? 'border-[#9B1421] bg-[#363232]'
                            : 'border-[#504A4A] bg-[#363232] hover:border-[#9B1421]/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-[#EBFFEE] font-medium">
                                {member.first_name || ''} {member.last_name || ''}
                                {!member.first_name && !member.last_name && <span className="text-[#A9A3A3] italic">(No name)</span>}
                              </p>
                              {isCurrentLead && (
                                <span className="px-2 py-0.5 text-xs bg-yellow-900 text-yellow-300 rounded">Current Lead</span>
                              )}
                              {isSelected && !isCurrentLead && (
                                <span className="px-2 py-0.5 text-xs bg-[#9B1421] text-white rounded">Selected</span>
                              )}
                            </div>
                            {member.email && (
                              <p className="text-sm text-[#A9A3A3] flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {member.email}
                              </p>
                            )}
                            {member.phone_number && (
                              <p className="text-sm text-[#A9A3A3] flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {member.phone_number}
                              </p>
                            )}
                          </div>
                          <div className="ml-4">
                            {isSelected ? (
                              <div className="w-5 h-5 rounded-full bg-[#9B1421] flex items-center justify-center">
                                <div className="w-2 h-2 rounded-full bg-white" />
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-[#504A4A]" />
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex gap-3 justify-end">
                  <Button
                    onClick={() => setShowEditFamilyLeadModal(false)}
                    variant="outline"
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateFamilyLead}
                    disabled={!selectedNewLeadId || updatingFamilyLead}
                    className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updatingFamilyLead ? 'Updating...' : 'Update Family Lead'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

